import { AIClient } from "@/ai/AIAssistant";
import { DestinationFolder, getEntryLabel } from "@/archive/archiveDomain";
import { ArchiveDocumentType } from "@/archive/commonArchiveDomain";
import {
  BR_DECISION_STATUS,
  CompanyItem,
  CustomizedFreetextType,
  emailValidator,
  Failure,
  FreetextType,
  GetProjectDocumentSignersResponse,
  isFailure,
  isOk,
  isPending,
  Ok,
  pending,
  ProjectDocumentPreview,
  Result,
  Signer,
  SigningMethod,
  SubscriptionTypeModel,
} from "@/common/common";
import {
  CompaniesForPreview,
  createEmailField,
  DestinationFolderMode,
  DocumentArchiveDestination,
  DocumentsForPreview,
  DocumentStorageDestination,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  SharepointDestinationModel,
  SignerModel,
  SignersForm,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import { DecisionType } from "@/common/decisions";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import { EventLog } from "@/common/eventLogs";
import { ShareType } from "@/common/share";
import {
  BatchPreviewModel,
  FileToPreview,
  toFileToPreview,
} from "@/components-v2/BatchPreviewDialog/batchPreviewDomain";
import { FilePreviewProps } from "@/components-v2/FilePreview";
import { supportsSelectionOfSigners } from "@/dbot/boardMeetingMinutes/boardMeetingMinutesDomain";
import {
  AdditionalBRSigner,
  AdditionalBRSignerReason,
  applyDecisionAction,
  ApplyDecisionAction,
  AttachmentFollowUp,
  AttachmentType,
  AutosignDocumentEvent,
  BRSigner,
  BRSignerRole,
  BundledDocument,
  checkIncorporationStatusEvent,
  CompanyRoleIdWithName,
  confirmSendIncorporationToBRAction,
  confirmSendToBRAction,
  ConfirmSendToBRAction,
  confirmSkipSendToBRAction,
  ConfirmSkipSendToBRAction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionAttachment,
  DecisionItem,
  deleteAttachmentEvent,
  DeleteAttachmentEvent,
  DeleteAttachmentResultEvent,
  DetailsPanel,
  DocumentItem,
  DocumentPreview,
  documentPreviewUrl,
  DocumentSigners,
  DocumentViewingStatus,
  DownloadAttachmentEvent,
  DownloadDocumentAction,
  DownloadSubscriptionFormsAction,
  DsePrekontrollErrorItem,
  DseSimulatedBrResponseKind,
  DseStatusResponse,
  DynamicRecipient,
  DynamicRecipientType,
  fetchIncorporationActivationInfoEvent,
  FollowUp,
  FollowUpStatus,
  followUpStatusLabels,
  FollowUpType,
  getDecisionAttachmentsDescription,
  getDecisionSendToBRDescription,
  GetDocumentViewingStatusResponse,
  GetSignableDocumentsResponse,
  getViewingStatusEvent,
  IncorporationActivationInfo,
  IncorporationActivationSubscription,
  IncorporationDocumentPart,
  IncreaseOfShareCapitalSubscribersStatus,
  isDecisionApplied,
  isIdentificationAttachment,
  isIncorporationDocument,
  mapToBRSigners,
  MeetingAuthMethod,
  OpenIncreaseOfShareCapitalSubscribersAllocationEvent,
  OpenIncreaseOfShareCapitalSubscribersStatusEvent,
  OpenReviewDialogAction,
  OpenSendForReviewDialogAction,
  OpenSigningStatusDialogAction,
  OpenSubscriptionFormBatchPreviewDialogAction,
  OpenSubscriptionStatusEvent,
  OpenViewingStatusDialogAction,
  ProjectContext,
  ProjectDocumentStatus,
  Recipient,
  RecipientId,
  RefreshSigningStatusesAction,
  requiredAttachmentLabel,
  ResendDocumentForViewing,
  resendNoticeOfBoardMeetingEvent,
  selectedDestinationFolderModel,
  selectIncorporationActivationSubscriptionEvent,
  sendDocumentForSigningEvent,
  sendDocumentForViewingEvent,
  SendForReviewEvent,
  SendForReviewMessageTemplate,
  sendMultipleDocumentsForSigningEvent,
  sendSubscriptionFormForSigningEvent,
  SendToBRDecision,
  SendToBRFollowUp,
  shareholdersWithSharesExcludingSelf,
  SharesForShareholder,
  simulateBrResponseEvent,
  SubscriptionFormFollowUp,
  SubscriptionFormStatusFollowUp,
  UnlockDocumentAction,
  updateBoardMeetingRecipientsEvent,
  updateCompanyRoleEmailsEvent,
  UpdateCompanyRoleItem,
  updateFollowUpStatusEvent,
  uploadAttachmentsEvent,
  UploadAttachmentsEvent,
  ValidateProjectAction,
  ViewSubscribersFollowUp,
  brSignerId,
} from "@/dbot/common/commonDomain";
import {
  filterSubscribersOnSelectedShareTypes,
  IncreaseOfShareCapitalType,
  PeriodForSubscription,
  SubscriberForIncreaseNominalValue,
  SubscriberForIssue,
  Subscription,
  ViewSubscribersDialogModel,
} from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { counterpartyOf, ownCompanyOf } from "@/dbot/merger/mergerDomain";
import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip";
import {
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  GetSubscriptionStatusResponse,
  SigningStatusForSigner,
  SubscriptionStatus,
} from "@/document/documentApi";
import { showGeneralError } from "@/events/eventsDomain";
import { FileUploadConfig, UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { formatDate } from "@/i18n/dateFormat";
import { DEFAULT_LANG, Language } from "@/i18n/languages";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { MeetingType } from "@/meetings/meetingsDomain";
import { SharepointContext } from "@/sharepoint/sharepointDomain";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { CompanyRole, CompanyroleId } from "@/types/CompanyInfo";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { EmailStatus } from "@/types/SavedDocument";
import { ShareholderDTO } from "@/types/Shareholder";
import { User } from "@/types/UserInfo";
import { next, prev } from "@/utils/arrays";
import { combineDateAndTime, isToday, plusDays, toDate } from "@/utils/Dates";
import { when } from "@/utils/functions";
import { logError } from "@/utils/log";
import { boardMemberBoardMeetings, joinUrl } from "@/utils/navigation";
import { getFullName, sortedBoardAndCEO, sortedRoles } from "@/utils/Roles";
import { isBlank, isNotBlank } from "@/utils/strings";
import { recipientIdFromUrnReference, resolveUrnReference } from "@/utils/UrnReference";
import {
  SignSubscriptionFormShare,
  SubscriptionModel,
} from "@/views/Pages/SubscriptionFormSign/subscriptionFormDomain";
import {
  compact,
  first,
  groupBy,
  identity,
  keyBy,
  mapValues,
  sortBy,
  sumBy,
  uniq,
  uniqBy,
} from "lodash";
import { action, makeAutoObservable, runInAction } from "mobx";
import { DocumentListItem } from "./SummaryController";

///
/// Dialogs
///

export type DialogState =
  | { type: "none" }
  | { type: "reviewSendNoticeOfGeneralMeeting"; model: ReviewSendNoticeOfGeneralMeetingDialogModel }
  | { type: "reviewSendNoticeOfBoardMeeting"; model: ReviewSendNoticeOfBoardMeetingDialogModel }
  | { type: "reviewSendSubscriptionForm"; model: ReviewSendSubscriptionFormDialogModel }
  | { type: "reviewDocumentSigning"; model: ReviewDocumentSigningDialogModel }
  | { type: "bundleSelection"; model: BundleSelectionDialogModel }
  | { type: "packageSigning"; model: PackageSigningDialogModel }
  | { type: "sendForReview"; model: SendForReviewDialogModel }
  | { type: "viewDocumentSigningStatus"; model: ViewDocumentSigningStatusDialogModel }
  | { type: "viewSubscriptionFormBatchPreview"; model: ViewSubscriptionFormBatchPreviewModel }
  | { type: "viewSubscriptionStatus"; model: ViewSubscriptionStatusDialogModel }
  | { type: "viewIncreaseOfShareCapitalSubscribersStatus"; model: ViewSubscribersDialogModel }
  | { type: "viewIncreaseOfShareCapitalSubscribersAllocation"; model: ViewSubscribersDialogModel }
  | { type: "viewDocumentViewingStatus"; model: ViewDocumentViewingStatusDialogModel }
  | { type: "reopenLockedDocument"; model: ReopenLockedDocumentDialogModel }
  | { type: "confirmSendToBR"; model: ConfirmSendToBRDialogModel }
  | {
      type: "confirmSendIncorporationToBR";
      model: ConfirmSendIncorporationToBRDialogModel;
    }
  | { type: "incorporationSendingReceipt"; confirmationDocumentCount: number };

export const noDialog = (): { type: "none" } => ({ type: "none" });

type ReviewSendNoticeOfGeneralMeetingDialogModelParams = {
  companyName: string;
  document: DocumentItem;
  hasInvalidCompanyRoleRecipients: boolean;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  shareholderRecipients: Recipient[];
  emailBody: TMessage;
  aiClient: AIClient;
  documentStatus?: ProjectDocumentStatus;
  isMissingShareholderSSN: boolean;
  hasForeignShareholders: boolean;
};

export class ReviewSendNoticeOfGeneralMeetingDialogModel {
  readonly params: ReviewSendNoticeOfGeneralMeetingDialogModelParams;
  recipients: RecipientsEditModel;
  emailBody: LocalizedTextEditorModel;
  updateEmailResult?: Result<TMessage, TMessage>;
  isSending = false;
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;

  constructor(params: ReviewSendNoticeOfGeneralMeetingDialogModelParams) {
    this.params = params;
    this.recipients = new RecipientsEditModel(params.shareholderRecipients);
    this.recipients.validate();
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: params.aiClient,
    });
    this.authMethod = new RadioFieldModel<MeetingAuthMethod | null>({
      value: params.documentStatus?.authMethod ?? null,
      options: [null, "PASSWORD", "LOGIN"],
      idFn: identity,
      labelFn: meetingAuthMethodLabel,
    });
    this.meetingPassword = new StringFieldModel({
      required: true,
      value: params.documentStatus?.meetingPassword ?? generatePinCode(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  init(t: TFunction) {
    this.emailBody.textModels.map((d) => d.model.setInputValue(t(this.params.emailBody)));
  }

  get companyName() {
    return this.params.companyName;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get title(): TMessage {
    return this.params.document.title;
  }

  get hasInvalidCompanyRoleRecipients() {
    return this.params.hasInvalidCompanyRoleRecipients;
  }

  get canSubmit() {
    return (
      !this.isSending &&
      this.recipients.isValid &&
      !this.hasInvalidCompanyRoleRecipients &&
      (this.authMethod.value === null ||
        (this.authMethod.value === "PASSWORD" && this.meetingPassword.isValid) ||
        (this.authMethod.value === "LOGIN" && !this.params.isMissingShareholderSSN))
    );
  }

  updateShareholderEmail(item: RecipientFieldModel) {
    item.emailField.validate();
    if (typeof item.id === "number" && item.emailField.isValid && item.isChanged) {
      const onFulfilled = action((result: Result<TMessage, TMessage>, email?: string) => {
        this.updateEmailResult = result;
        if (isOk(this.updateEmailResult)) {
          item.emailField = createEmailField({ value: email, required: true });
          if (this.isSending) {
            this.sendForViewing();
          }
        } else if (isFailure(this.updateEmailResult)) {
          this.isSending = false;
        }
      });
      this.updateEmailResult = pending();
      if (item.id)
        this.params.dispatch({
          type: "dbot/update-shareholder-email",
          shareholderId: item.id,
          email: item.emailField.inputValue,
          onFulfilled: onFulfilled,
        });
    }
  }

  submit() {
    this.recipients.validate();
    if (this.canSubmit) {
      this.isSending = true;
      if (!isPending(this.updateEmailResult)) {
        this.sendForViewing();
      }
    }
  }

  sendForViewing() {
    this.params.dispatch(
      sendDocumentForViewingEvent(
        this.params.document,
        `${this.emailBody.textModels[0].model.inputValue}`,
        this.authMethod.value ? this.authMethod.value : undefined,
        this.authMethod.value === "PASSWORD" ? this.meetingPassword.validatedValue : undefined,
      ),
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-document-for-viewing-result":
        this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        this.params.closeDialog();
        break;
    }
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }
}

type ReviewSendNoticeOfBoardMeetingDialogModelParams = {
  document: DocumentItem;
  recipients: Recipient[];
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  title: TMessage;
  emailBody: TMessage;
  aiClient: AIClient;
  isResend?: boolean;
  hasAllSocialSecurityNumbers: boolean;
  hasExtraParticipants: boolean;
  authMethod?: MeetingAuthMethod;
  meetingPassword?: string;
  isSendToNewParticipants?: boolean;
};

export class ReviewSendNoticeOfBoardMeetingDialogModel {
  private readonly params: ReviewSendNoticeOfBoardMeetingDialogModelParams;
  recipients: RecipientsEditModel;
  emailBody: LocalizedTextEditorModel;
  isResend: boolean;
  isSendToNewParticipants: boolean;
  isSending = false;
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;

  constructor(params: ReviewSendNoticeOfBoardMeetingDialogModelParams) {
    this.params = params;
    this.recipients = new RecipientsEditModel(params.recipients);
    this.recipients.validate();
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: params.aiClient,
    });
    this.isResend = params.isResend ?? false;
    this.isSendToNewParticipants = params.isSendToNewParticipants ?? false;
    this.authMethod = new RadioFieldModel<MeetingAuthMethod | null>({
      value: params.authMethod ?? null,
      options: [null, "PASSWORD", "LOGIN"],
      idFn: identity,
      labelFn: meetingAuthMethodLabel,
    });
    this.meetingPassword = new StringFieldModel({
      required: true,
      value: params.meetingPassword ?? generatePinCode(),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init(t: TFunction) {
    this.emailBody.textModels.map((d) => d.model.setInputValue(t(this.params.emailBody)));
  }

  get title() {
    return this.params.title;
  }

  get emailBodyValue() {
    return this.emailBody.textModels[0].model.inputValue;
  }

  get canSubmit() {
    return (
      !this.isSending &&
      this.emailBodyValue !== "" &&
      (this.recipients.isValid || this.isResend) &&
      (this.authMethod.value === null ||
        (this.authMethod.value === "PASSWORD" && this.meetingPassword.isValid) ||
        (this.authMethod.value === "LOGIN" &&
          this.hasAllSocialSecurityNumbers &&
          !this.hasExtraParticipants))
    );
  }

  get hasAllSocialSecurityNumbers() {
    return this.params.hasAllSocialSecurityNumbers;
  }

  get hasExtraParticipants() {
    return this.params.hasExtraParticipants;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get showAccessTypeAndRecipients() {
    return !this.isResend;
  }

  submit() {
    if (this.isResend) {
      this.resendDocumentForViewing();
    } else {
      this.sendDocumentForViewing();
    }
  }

  resendDocumentForViewing() {
    this.isSending = true;
    this.params.dispatch(
      resendNoticeOfBoardMeetingEvent(this.params.document, `${this.emailBodyValue}`),
    );
  }

  sendDocumentForViewing() {
    this.meetingPassword.validate();
    this.recipients.validate();
    if (!this.canSubmit) {
      return;
    }

    this.isSending = true;

    if (this.recipients.isChanged) {
      this.params.dispatch(
        updateBoardMeetingRecipientsEvent(this.recipients.getChangedRecipients()),
      );
    } else {
      this.doSendDocumentForViewing();
    }
  }

  doSendDocumentForViewing() {
    this.params.dispatch(
      sendDocumentForViewingEvent(
        this.params.document,
        this.emailBodyValue,
        this.authMethod.value ?? undefined,
        this.authMethod.value === "PASSWORD" ? this.meetingPassword.validatedValue : undefined,
        this.isResend || this.isSendToNewParticipants,
      ),
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-document-for-viewing-result":
      case "dbot/resend-notice-of-board-meeting-result":
        this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        this.params.closeDialog();
        break;
      case "dbot/update-board-meeting-recipients-result":
        this.isSending = false;
        if (isOk(event.result)) {
          this.doSendDocumentForViewing();
        } else {
          this.recipients.error = event.result.error;
        }
        break;
    }
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }
}

export function meetingAuthMethodLabel(type: MeetingAuthMethod | null): TMessage {
  switch (type) {
    case null:
      return { key: "dbot.meeting.access-open" };
    default:
      return { key: "dbot.meeting.access-password" };
  }
}

type ReviewSendSubscriptionFormDialogModelParams = {
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  aiClient: AIClient;
  user: User | null;
  signers: Signer[];
};

export class ReviewSendSubscriptionFormDialogModel {
  private readonly params: ReviewSendSubscriptionFormDialogModelParams;
  form: SignersForm;
  isSending = false;

  constructor(params: ReviewSendSubscriptionFormDialogModelParams) {
    this.params = params;
    this.form = new SignersForm({
      signers: params.signers,
      aiClient: params.aiClient,
      user: this.params.user,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get title() {
    return this.params.document.title;
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }

  get canSubmit() {
    return !this.isSending && this.form.isValid;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  submit() {
    this.form.validate();
    if (!this.canSubmit) {
      return;
    }
    this.isSending = true;
    this.params.dispatch(
      sendSubscriptionFormForSigningEvent(this.params.document, this.form.toSigners()),
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-subscription-form-for-signing-result":
        this.params.closeDialog();
        break;
    }
  }
}

type ViewSubscriptionFormBatchPreviewModelParams = {
  title: TMessage | string;
  filePreview: (file: FileToPreview) => FilePreviewProps;
  context: ProjectContext;
  closeDialog: () => void;
  subscribersData: IncreaseOfShareCapitalSubscribersStatus;
};

export class ViewSubscriptionFormBatchPreviewModel {
  documentPreviewModel: BatchPreviewModel;
  subscriptions: Map<string, SubscriptionModel> = new Map();
  selectedTab: string = "document";

  constructor(readonly params: ViewSubscriptionFormBatchPreviewModelParams) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.documentPreviewModel = new BatchPreviewModel(params.title, params.filePreview);
    this.initSubscribers(params.subscribersData);
  }

  get company() {
    return this.params.context.initialState.company;
  }

  get shareholders() {
    return this.params.context.initialState.shareholders;
  }

  getShareType(shareTypeId: number) {
    return this.params.context.initialState.shareTypes.find((st) => st.sharetypeId === shareTypeId);
  }

  setSelectedTab(value: string) {
    this.selectedTab = value;
  }

  get isDocumentTab() {
    return this.selectedTab === "document";
  }

  get isElectronicFormTab() {
    return this.selectedTab === "electronicForm";
  }

  get selectedSubscription() {
    return this.documentPreviewModel.selectedFile !== undefined
      ? (this.subscriptions.get(this.documentPreviewModel.selectedFile.reference) ?? null)
      : null;
  }

  getSubscriberName(subscriber: SubscriberForIssue | SubscriberForIncreaseNominalValue) {
    const shareholder = this.params.context.initialState.shareholders.find(
      (s) => s.id === subscriber.shareholderId,
    );
    return shareholder?.name ?? subscriber.name ?? "";
  }

  initSubscribers(data: IncreaseOfShareCapitalSubscribersStatus) {
    if (data.type === IncreaseOfShareCapitalType.shareIssue) {
      const subscriberForIssues = filterSubscribersOnSelectedShareTypes(
        data.subscribers,
        data.selectedShareTypes,
      );
      this.documentPreviewModel.setFiles(
        subscriberForIssues.map((subscriber) =>
          toFileToPreview(this.getSubscriberName(subscriber), subscriber.subscriberid),
        ),
      );
      subscriberForIssues.forEach((subscriber) =>
        this.subscriptions.set(
          subscriber.subscriberid,
          this.toSubscriptionModel(
            subscriber,
            data.isPreferentialRights,
            data.isOverSubscriptionAllowed,
            data.subscriptionPeriod,
          ),
        ),
      );
    } else {
      this.documentPreviewModel.setFiles(
        data.subscribers.map((subscriber) =>
          toFileToPreview(this.getSubscriberName(subscriber), subscriber.subscriberid),
        ),
      );
    }
  }

  toSubscriptionModel(
    subscriber: SubscriberForIssue,
    isPreferentialRights: boolean,
    isOverSubscriptionAllowed: boolean,
    subscriptionPeriod: PeriodForSubscription,
  ) {
    return new SubscriptionModel({
      signerName: subscriber?.name ?? "",
      isShareIssue: true,
      isPreferentialRights: isPreferentialRights,
      isOversubscriptionAvailable: isOverSubscriptionAllowed,
      subscriptionPeriod: {
        from: subscriptionPeriod.from,
        to: subscriptionPeriod.to,
        hasStarted: subscriptionPeriod.from ? toDate(subscriptionPeriod.from) <= new Date() : false,
        hasEnded: subscriptionPeriod.to
          ? toDate(subscriptionPeriod.to) < new Date() && !isToday(subscriptionPeriod.to)
          : false,
      },
      subscriber: {
        name: subscriber?.name ?? "",
        organisationNumber: subscriber?.organisationNumber ?? "",
      },
      shares: subscriber?.subscriptions.map((subscription) => this.toShares(subscription)) ?? [],
      shareholder: this.shareholders.find((d) => d.id === subscriber.shareholderId),
    });
  }

  toShares(subscription: Subscription): SignSubscriptionFormShare {
    return {
      sharetypeId: subscription.sharetypeId,
      sharetypeCode: subscription.shareTypeCode,
      sharePrice: subscription.subscriptionPrice,
      availableShares: subscription.numberOfShares,
      numberOfShares: subscription.numberOfShares,
      shareType: this.getShareType(subscription.sharetypeId),
    };
  }

  close() {
    this.params.closeDialog();
  }
}

type ReviewDocumentSigningDialogModelParams = {
  orgNo: string;
  document: DocumentItem;
  signers: Signer[];
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  infoMessage?: TMessage;
  showUnsupportedAttachmentWarning?: boolean;
  aiClient: AIClient;
  emailBody: TMessage;
  user: User | null;
  isBatchProject: boolean;
  sharepointContext?: SharepointContext;
  destinationFolder?: DestinationFolder;
  destinationFolderModel?: RadioFieldModel<DestinationFolder | undefined>;
  lockedSigningMethod?: SigningMethod;
  openReceiptDialog?: () => void;
  attachments?: UploadedAttachment[];
};

export class ReviewDocumentSigningDialogModel {
  readonly params: ReviewDocumentSigningDialogModelParams;
  form: SignersForm;
  isSending = false;
  selectedDocument: ProjectDocumentPreview | undefined;
  selectedCompany: CompanyItem | undefined;

  constructor(params: ReviewDocumentSigningDialogModelParams) {
    this.params = params;
    this.setSelectedCompany(this.companies[0]);
    this.selectedDocument = this.documents.length > 0 ? this.documents[0] : undefined;
    const groupByRecipient = !params.isBatchProject && params.documents.some((d) => d.reference);
    this.form = new SignersForm({
      ...params,
      signers: groupByRecipient
        ? tagSignersWithDocument(params.signers, params.document.documentId, params.documents)
        : params.signers,
      groupByDocument: groupByRecipient,
      sendSealedCopyToDefaultSelection: params.isBatchProject ? [] : undefined,
      showBatchEmailsSettings: params.isBatchProject,
      sharepointContext: params.sharepointContext,
      selectedDestinationFolder: params.destinationFolder,
      isBatchSigning: params.isBatchProject,
      destinationFolderModel: params.destinationFolderModel,
      lockedSigningMethod: params.lockedSigningMethod,
      sealedCopyAttachments: params.attachments?.map((a) => ({
        ...a,
        documentId: params.document.documentId,
      })),
      showDestinationFolderSelector:
        params.document.documentData?.type === DocumentType.custom_document,
      companiesItemsModel: params.isBatchProject
        ? new RadioFieldModel<CompanyItem>({
            value: params.companies[0],
            options: params.companies,
            idFn: (option) => option.organisationNumber,
            labelFn: (option) => ({ translated: option.companyName }),
            iconFn: () => "buildings",
            onChange: (option) => {
              const selectedOrgNo = option.organisationNumber;
              this.params.dispatch([
                {
                  type: "dbot/fetch-destination-folders",
                  orgNo: selectedOrgNo,
                  onSuccess: (res) => {
                    runInAction(() => {
                      if (
                        this.form.companiesItemsModel?.value?.organisationNumber === selectedOrgNo
                      ) {
                        this.form.destinationFolderModel = selectedDestinationFolderModel(
                          res.entries,
                        );
                      }
                    });
                    return [];
                  },
                  onFailure: [showGeneralError()],
                },
              ]);
            },
          })
        : undefined,
      onFolderModeChange: params.isBatchProject
        ? (mode) => this.onFolderModeChange(mode)
        : undefined,
    });
    this.form.validate();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  onFolderModeChange(mode: DestinationFolderMode) {
    if (mode !== DestinationFolderMode.PER_COMPANY_FOLDER) {
      this.form.companyFolderModels = [];
      return;
    }
    const companies = this.params.companies;
    this.form.companyFolderModels = companies.map((company) => ({ company, isLoading: true }));
    companies.forEach((company) => {
      this.params.dispatch([
        {
          type: "dbot/fetch-destination-folders",
          orgNo: company.organisationNumber,
          onSuccess: (res) => {
            runInAction(() => {
              const entry = this.form.companyFolderModels.find(
                (e) => e.company.organisationNumber === company.organisationNumber,
              );
              if (entry) {
                entry.isLoading = false;
                entry.folderModel = new RadioFieldModel<DestinationFolder | undefined>({
                  value: res.entries[0],
                  options: res.entries,
                  idFn: (o) => o?.id ?? "",
                  labelFn: (o) => (o ? getEntryLabel(o) : { key: "select" }),
                  iconFn: (o) => (o?.rootFolder ? "folder" : "archive"),
                });
              }
            });
            return [];
          },
          onFailure: [showGeneralError()],
        },
      ]);
    });
  }

  get companies() {
    return this.params.companies.filter((company) =>
      this.params.documents.some((document) => document.orgNo === company.organisationNumber),
    );
  }

  get companiesForSigning(): CompaniesForPreview | undefined {
    return this.selectedCompany !== undefined
      ? {
          companies: this.companies,
          selectedCompany: this.selectedCompany,
          selectCompany: this.setSelectedCompany,
          previousCompany: when(
            prev(
              this.companies,
              (s) => s.organisationNumber === this.selectedCompany?.organisationNumber,
            ),
            (s) => () => this.setSelectedCompany(s),
          ),
          nextCompany: when(
            next(
              this.companies,
              (s) => s.organisationNumber === this.selectedCompany?.organisationNumber,
            ),
            (s) => () => this.setSelectedCompany(s),
          ),
        }
      : undefined;
  }

  setSelectedCompany(company?: CompanyItem) {
    this.selectedCompany = company;
    if (this.documents.length > 0) {
      this.setSelectedDocument(this.documents[0]);
    }
  }

  get documents() {
    return this.selectedCompany
      ? this.params.documents.filter(
          (document) => document.orgNo === this.selectedCompany?.organisationNumber,
        )
      : this.params.documents;
  }

  get documentsForSigning(): DocumentsForPreview | undefined {
    return this.selectedDocument !== undefined
      ? {
          documents: this.documents.map((a) => ({
            id: a.documentId,
            name: a.documentTitle,
            reference: a.reference,
          })),
          selectedDocument: {
            id: this.selectedDocument.documentId,
            name: this.selectedDocument.documentTitle,
            reference: this.selectedDocument.reference,
          },
          selectDocument: (document) =>
            this.setSelectedDocument(this.documentById(document.id, document.reference)),
          previousDocument: when(
            prev(
              this.documents,
              (s) =>
                s.documentId === this.selectedDocument?.documentId &&
                s.reference === this.selectedDocument?.reference,
            ),
            (s) => () => this.setSelectedDocument(s),
          ),
          nextDocument: when(
            next(
              this.documents,
              (s) =>
                s.documentId === this.selectedDocument?.documentId &&
                s.reference === this.selectedDocument?.reference,
            ),
            (s) => () => this.setSelectedDocument(s),
          ),
        }
      : undefined;
  }

  setSelectedDocument(document?: ProjectDocumentPreview) {
    this.selectedDocument = document;
  }

  documentById(id: string, reference?: string) {
    return this.documents.find((a) => a.documentId === id && a.reference === reference);
  }

  get showUnsupportedAttachmentWarning() {
    return this.params.showUnsupportedAttachmentWarning;
  }

  get title(): TMessage {
    return this.params.document.title;
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }

  get canSubmit() {
    return !this.isSending && this.form.isValid;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get infoMessage() {
    return this.params.infoMessage;
  }

  documentPreview({ t }: { t: TFunction }): FilePreviewProps | undefined {
    if (this.selectedDocument !== undefined) {
      const reference = resolveUrnReference(this.selectedDocument?.reference);
      return {
        filename: `${this.selectedDocument.documentTitle}.pdf`,
        file: {
          url: documentPreviewUrl({
            documentId: this.selectedDocument.documentId,
            orgNo: this.selectedDocument.orgNo,
            recipientId: recipientIdFromUrnReference(reference),
            includeAttachments: !this.showUnsupportedAttachmentWarning,
          }),
        },
      };
    } else {
      return {
        filename: `${t(this.params.document.title)}.pdf`,
        file: {
          url: documentPreviewUrl({
            documentId: this.params.document.documentId,
            orgNo: this.params.orgNo,
            includeAttachments: !this.showUnsupportedAttachmentWarning,
          }),
        },
      };
    }
  }

  submit() {
    this.form.validate();

    if (!this.canSubmit) {
      return;
    }

    this.isSending = true;

    if (this.form.hasChangedEmails) {
      this.params.dispatch(
        updateCompanyRoleEmailsEvent(
          this.params.orgNo,
          compact(this.form.getSignersWithChangedEmail().map(mapSignerToUpdateCompanyRoleItem)),
        ),
      );
    } else {
      this.params.dispatch(
        sendDocumentForSigningEvent(
          this.params.document,
          this.form.toSigners(),
          this.form.emailBody.textModels[0].model.inputValue,
          this.form.toSigningSettings(),
        ),
      );
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-company-role-emails-result":
        if (isOk(event.result)) {
          this.params.dispatch(
            sendDocumentForSigningEvent(
              this.params.document,
              this.form.toSigners(),
              this.form.emailBody.textModels[0].model.inputValue,
              this.form.toSigningSettings(),
            ),
          );
        } else {
          this.isSending = false;
          this.form.error = event.result.error;
        }
        break;
      case "dbot/send-document-for-signing-result":
        if (isOk(event.result)) {
          if (this.params.openReceiptDialog) {
            this.params.openReceiptDialog();
            break;
          }
          this.params.dispatch({
            type: "show-alert",
            message: { key: "dbot.sendDocumentForSigningSuccess" },
            messageType: "success",
          });
        }
        this.params.closeDialog();
        break;
    }
  }
}

export type BundleSelectionDialogModelParams = {
  currentDocument: DocumentItem;
  documents: BundledDocument[];
  companies: CompanyItem[];
  emailBody: TMessage;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
};

export class BundleSelectionDialogModel {
  readonly params: BundleSelectionDialogModelParams;
  selected: Set<string>;

  constructor(params: BundleSelectionDialogModelParams) {
    this.params = params;
    // Only the document the user clicked send on is pre-selected.
    this.selected = new Set(params.documents.filter((d) => d.isCurrent).map((d) => d.documentId));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get documents(): BundledDocument[] {
    return this.params.documents;
  }

  isChecked(documentId: string): boolean {
    return this.selected.has(documentId);
  }

  toggle(documentId: string) {
    if (this.selected.has(documentId)) {
      this.selected.delete(documentId);
    } else {
      this.selected.add(documentId);
    }
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  // Names of recipients shared between the current doc and at least one other ready doc.
  private get sharedRecipientNames(): string[] {
    const current = this.params.documents.find((d) => d.isCurrent);
    if (!current) return [];
    const otherEmails = toRecipientEmailSet(
      this.params.documents
        .filter((d) => !d.isCurrent)
        .flatMap((d) => d.signers.map((s) => s.email)),
    );
    return current.signers
      .filter((s) => otherEmails.has(normalizeEmail(s.email)))
      .map((s) => s.name)
      .filter((name, i, arr) => arr.indexOf(name) === i);
  }

  get descriptionMessage(): TMessage {
    const names = this.sharedRecipientNames;
    if (names.length === 1) {
      return { key: "dbot.send-together.description-one-name", params: { name: names[0] } };
    }
    if (names.length === 2) {
      return {
        key: "dbot.send-together.description-two-names",
        params: { name1: names[0], name2: names[1] },
      };
    }
    return { key: "dbot.send-together.description_other", params: { count: names.length } };
  }

  // Emails of signers that appear in more than one bundled document.
  private get multiDocumentSignerEmails(): Set<string> {
    const documentCountByEmail = new Map<string, number>();
    this.params.documents.forEach((doc) => {
      toRecipientEmailSet(doc.signers.map((s) => s.email)).forEach((email) => {
        documentCountByEmail.set(email, (documentCountByEmail.get(email) ?? 0) + 1);
      });
    });
    return new Set(
      [...documentCountByEmail.entries()].filter(([, count]) => count > 1).map(([email]) => email),
    );
  }

  // Signers with multiple documents to sign are listed first; the rest keep their order.
  sortedSigners(document: BundledDocument): Signer[] {
    const multiDocumentEmails = this.multiDocumentSignerEmails;
    return sortBy(document.signers, (s) =>
      multiDocumentEmails.has(normalizeEmail(s.email)) ? 0 : 1,
    );
  }

  isMultiDocumentSigner(signer: Signer): boolean {
    return this.multiDocumentSignerEmails.has(normalizeEmail(signer.email));
  }

  close() {
    this.params.closeDialog();
  }

  submit() {
    const selectedDocuments = this.params.documents.filter((d) => this.selected.has(d.documentId));
    this.params.dispatch({
      type: "dbot/fetch-destination-folders",
      onSuccess: (res) => [
        {
          type: "dbot/open-package-signing-dialog",
          documents: selectedDocuments,
          companies: this.params.companies,
          emailBody: this.params.emailBody,
          destinationFolders: res.entries,
        },
      ],
      onFailure: [showGeneralError()],
    });
  }
}

export type PackageSigningDialogModelParams = {
  orgNo: string;
  documents: BundledDocument[];
  companies: CompanyItem[];
  emailBody: TMessage;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  aiClient: AIClient;
  user: User | null;
  isBatchProject: boolean;
  destinationFolders: DestinationFolder[];
  sharepointContext: (documentType: DocumentType) => SharepointContext;
};

export class PackageSigningDialogModel {
  readonly params: PackageSigningDialogModelParams;
  form: SignersForm;
  documentDestinations: DocumentStorageDestination[];
  isSending = false;
  selectedPreview?: ProjectDocumentPreview;

  constructor(params: PackageSigningDialogModelParams) {
    this.params = params;
    this.selectedPreview = first(this.previews);
    const destinationFolderModel = params.documents.some(
      (d) => d.documentType === DocumentType.custom_document,
    )
      ? selectedDestinationFolderModel(params.destinationFolders)
      : undefined;
    this.form = new SignersForm({
      signers: params.documents.flatMap((d) => d.signers),
      aiClient: params.aiClient,
      user: params.user,
      emailBody: params.emailBody,
      showBatchEmailsSettings: params.documents.length > 1,
      isBatchSigning: true,
      groupByDocument: true,
      sealedCopyAttachments: params.documents.flatMap((d) =>
        d.attachments.map((a) => ({ ...a, documentId: d.documentId, documentTitle: d.title })),
      ),
      destinationFolderModel,
    });
    this.documentDestinations = documentStorageDestinations(
      params.documents,
      destinationFolderModel,
      params.sharepointContext,
    );
    this.form.validate();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get previews(): ProjectDocumentPreview[] {
    return this.params.documents.flatMap((d) => d.previews);
  }

  get title(): TMessage {
    return {
      key: "dbot.send-together.package.title",
      params: { count: this.params.documents.length },
    };
  }

  get canClose() {
    return !this.isSending;
  }

  get canSubmit() {
    return !this.isSending && this.form.isValid;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  // Warn if any bundled document has non-PDF attachments that will be dropped from the sealed PDF.
  get showUnsupportedAttachmentWarning() {
    return this.params.documents.some((d) => d.hasUnsupportedAttachments);
  }

  private hasUnsupportedAttachmentsForPreview(preview: ProjectDocumentPreview): boolean {
    return (
      this.params.documents.find((d) => d.previews.some((p) => p.documentId === preview.documentId))
        ?.hasUnsupportedAttachments ?? false
    );
  }

  setSelectedPreview(preview?: ProjectDocumentPreview) {
    this.selectedPreview = preview;
  }

  get documentsForSigning(): DocumentsForPreview | undefined {
    const selected = this.selectedPreview;
    if (!selected) {
      return undefined;
    }
    const docs = this.previews;
    const toItem = (p: ProjectDocumentPreview) => ({
      id: p.documentId,
      name: p.documentTitle,
      reference: p.reference,
    });
    const matches = (p: ProjectDocumentPreview) =>
      p.documentId === selected.documentId && p.reference === selected.reference;
    return {
      documents: docs.map(toItem),
      selectedDocument: toItem(selected),
      selectDocument: (d) =>
        this.setSelectedPreview(
          docs.find((p) => p.documentId === d.id && p.reference === d.reference),
        ),
      previousDocument: when(prev(docs, matches), (p) => () => this.setSelectedPreview(p)),
      nextDocument: when(next(docs, matches), (p) => () => this.setSelectedPreview(p)),
    };
  }

  documentPreview(): FilePreviewProps | undefined {
    const selected = this.selectedPreview;
    if (!selected) {
      return undefined;
    }
    return {
      filename: `${selected.documentTitle}.pdf`,
      file: {
        url: documentPreviewUrl({
          documentId: selected.documentId,
          orgNo: selected.orgNo,
          recipientId: recipientIdFromUrnReference(selected.reference),
          includeAttachments: !this.hasUnsupportedAttachmentsForPreview(selected),
        }),
      },
    };
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }

  submit() {
    this.form.validate();
    if (!this.canSubmit) {
      return;
    }
    this.isSending = true;
    this.form.syncDuplicates();
    const grouped = groupBy(this.form.toSigners(), (s) => s.documentId ?? "");
    const documents: DocumentSigners[] = Object.entries(grouped).map(
      ([documentId, docSigners]) => ({
        documentId,
        signers: docSigners,
        excludedAttachmentIds: this.form.excludedAttachmentIds(documentId),
      }),
    );
    this.params.dispatch(
      sendMultipleDocumentsForSigningEvent(
        documents,
        this.form.emailBody.textModels[0].model.inputValue,
        this.form.toSigningSettings(),
      ),
    );
  }

  handleEvent(event: DbotEvent) {
    if (event.type === "dbot/send-multiple-documents-for-signing-result") {
      if (isOk(event.result)) {
        this.params.dispatch({
          type: "show-alert",
          message: { key: "dbot.sendDocumentForSigningSuccess" },
          messageType: "success",
        });
      }
      this.params.closeDialog();
    }
  }
}

function documentStorageDestinations(
  documents: BundledDocument[],
  destinationFolderModel: RadioFieldModel<DestinationFolder | undefined> | undefined,
  sharepointContext: (documentType: DocumentType) => SharepointContext,
): DocumentStorageDestination[] {
  const sharepointByType = mapValues(
    keyBy(documents, (d) => d.documentType),
    (d) => new SharepointDestinationModel(sharepointContext(d.documentType)),
  );
  return documents.map((d) => ({
    documentId: d.documentId,
    title: d.title,
    archive: documentArchiveDestination(d.documentType, destinationFolderModel),
    sharepoint: sharepointByType[d.documentType],
  }));
}

function documentArchiveDestination(
  documentType: DocumentType,
  destinationFolderModel: RadioFieldModel<DestinationFolder | undefined> | undefined,
): DocumentArchiveDestination | undefined {
  switch (documentType) {
    case DocumentType.board_meeting:
      return { type: "fixed", label: "archive.board-archive" };
    case DocumentType.general_meeting:
      return { type: "fixed", label: "archive.shareholder-archive" };
    case DocumentType.custom_document:
      return destinationFolderModel && { type: "folder", folderModel: destinationFolderModel };
    default:
      return undefined;
  }
}

export type SendForReviewDialogModelParams = {
  baseUrl: string;
  orgNo: string;
  projectId: string;
  template: SendForReviewMessageTemplate;
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  roles: CompanyRole[];
  canBeSentForVoting: boolean;
  recipients?: CompanyRoleIdWithName[];
  context: ProjectContext;
};

export type SendForReviewDialogView = "options" | "recipients";

export type OptionsBeforeSending = {
  mode: "commenting" | "voting";
  deadlineForFeedback?: string;
};

export class RecipientModel {
  name: StringFieldModel;
  email: StringFieldModel;

  constructor() {
    makeAutoObservable(this);
    this.name = new StringFieldModel({ required: true });
    this.email = new StringFieldModel({
      required: true,
      validator: emailValidator,
    });
  }

  get isValid(): boolean {
    return this.name.isValid && this.email.isValid;
  }

  validate() {
    this.name.validate();
    this.email.validate();
    return this.isValid;
  }
}

export class SendForReviewDialogModel {
  readonly params: SendForReviewDialogModelParams;
  error?: TMessage;
  isSending?: boolean;
  view: SendForReviewDialogView = "options";
  subject = "";
  body = "";
  link = "";
  excludedRecipients = new Set<CompanyroleId>();
  deadlineForFeedback = false;
  enabledVotingOnDecisionMatters = false;
  deadlineDate: LocalDateFieldModel;
  deadlineTime: TimeFieldModel;
  disableVoting = false;
  synchronizeDeadlineDateInBody = true;

  extraRecipients: RecipientModel[] = [];

  constructor(params: SendForReviewDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
    this.deadlineDate = new LocalDateFieldModel({ required: this.deadlineForFeedback });
    this.deadlineTime = new TimeFieldModel({
      value: "",
    });
    this.recipients
      .filter(
        (role) =>
          params.recipients?.findIndex((r) => r.companyroleId === role.companyroleId) === -1,
      )
      .forEach((r) => this.toggleRecipient(r.companyroleId));
  }

  init(t: TFunction) {
    this.subject = t(this.params.template.subject);
    this.body = t(this.params.template.body);
    this.link = `[${t(this.params.template.linkText)}](${joinUrl(
      this.params.baseUrl,
      boardMemberBoardMeetings(this.params.orgNo, this.params.projectId, "minutes"),
    )})`;
  }

  toggleDeadlineForFeedback() {
    this.deadlineForFeedback = !this.deadlineForFeedback;
    this.deadlineDate.setRequired(this.deadlineForFeedback);
  }

  toggleEnableVotingOnDecisionMatter() {
    this.enabledVotingOnDecisionMatters = !this.enabledVotingOnDecisionMatters;
  }

  updateDeadlineFeedbackDate(t: TFunction) {
    if (!this.deadlineDate.isEmpty && this.synchronizeDeadlineDateInBody) {
      this.body = this.toText(t);
    }
  }

  private toText(t: TFunction) {
    return t("dbot.send-for-review.board-minutes.message-body", {
      deadline: formatDate(this.params.context.locale, this.deadlineDate.inputValue),
      senderName: this.params.template.senderName,
    });
  }

  get recipients(): CompanyRole[] {
    return sortedRoles(this.params.roles);
  }

  isCompanyRole(row: RecipientModel | CompanyRole): row is CompanyRole {
    return "companyroleId" in row;
  }

  getId(row: RecipientModel | CompanyRole): string | number {
    if (this.isCompanyRole(row)) {
      return row.companyroleId;
    } else {
      return this.extraRecipients.indexOf(row);
    }
  }

  get recipientCount() {
    return this.recipients.length + this.extraRecipients.length - this.excludedRecipients.size;
  }

  get viewOptions(): SendForReviewDialogView[] {
    return ["options", "recipients"];
  }

  get votingOnDecisionMatterLabel(): TMessage {
    if (this.params.context.currentDecisions.length === 1) {
      return {
        key: "commenting.matter.name",
        params: { name: first(this.params.context.currentDecisions)?.title },
      };
    }
    return {
      key: "commenting.count",
      params: { count: this.params.context.currentDecisions.length },
    };
  }

  get disableVotingOnMatter(): boolean {
    return this.params.context.currentDecisions.length < 1 && !this.disableVoting;
  }

  get canSubmit(): boolean {
    return this.recipientCount === 0 && this.extraRecipients.length === 0;
  }

  get isAllSelected(): boolean {
    return this.recipients.every((r) => !this.excludedRecipients.has(r.companyroleId));
  }

  toggleAllRecipients(): void {
    if (this.isAllSelected) {
      this.recipients.forEach((recipient) => this.excludedRecipients.add(recipient.companyroleId));
    } else {
      this.excludedRecipients.clear();
    }
  }

  setView(v: SendForReviewDialogView) {
    this.view = v;
  }

  infoButtonMessage(): TMessage {
    if (!this.disableVotingOnMatter) {
      return { key: "commenting.info" };
    }
    return { key: "commenting.info.not.found" };
  }

  viewLabel(v: SendForReviewDialogView): TMessage {
    switch (v) {
      case "options":
        return { key: "options" };
      case "recipients":
        return {
          key: "dbot.send-for-review.board-minutes.recipients",
          params: { n: this.recipientCount },
        };
    }
  }

  setBody(value: string, t: TFunction) {
    const text = this.toText(t);
    this.synchronizeDeadlineDateInBody = isBlank(this.deadlineDate.inputValue) || text === value;
    this.body = value;
  }

  toggleRecipient(id: CompanyroleId) {
    if (this.excludedRecipients.has(id)) {
      this.excludedRecipients.delete(id);
    } else {
      this.excludedRecipients.add(id);
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-for-review-result":
        if (isOk(event.result)) {
          this.params.closeDialog();
          this.params.dispatch({
            type: "show-alert",
            messageType: "success",
            message: { key: "dbot.send-for-review.success" },
          });
        } else {
          this.isSending = false;
          this.error = { key: "dbot.send-for-review.error" };
        }
        break;
    }
  }

  prepareData(language: Language): SendForReviewEvent {
    return {
      type: "dbot/send-for-review",
      documentId: this.params.document.documentId,
      sendMessageRequest: {
        language: language,
        messageSubject: this.subject,
        payload: `${this.body}`,
        isForBoardMembers: true,
        recipientSelectionState: {
          excludeBoardMemberIds: Array.from(this.excludedRecipients),
          inlineRecipients: this.extraRecipients.map((r) => ({
            name: r.name.validatedValue,
            email: r.email.validatedValue,
          })),
        },
      },
      options: {
        mode: this.enabledVotingOnDecisionMatters ? "voting" : "commenting",
        deadlineForFeedback: this.deadlineForFeedback
          ? isNotBlank(this.deadlineTime.validatedValue)
            ? combineDateAndTime(
                this.deadlineDate.validatedValue!,
                this.deadlineTime.validatedValue!,
              )
            : combineDateAndTime(this.deadlineDate.validatedValue!, "00:00")
          : undefined,
      },
    };
  }

  addRecipient() {
    this.extraRecipients.push(new RecipientModel());
  }

  canDeleteRecipient(row: RecipientModel | CompanyRole) {
    return !this.isCompanyRole(row);
  }

  deleteRecipient(row: RecipientModel | CompanyRole) {
    if (!this.isCompanyRole(row)) {
      this.extraRecipients.splice(this.extraRecipients.indexOf(row), 1);
    }
  }

  validate(): boolean {
    return [this.deadlineDate, ...this.extraRecipients].reduce(
      (acc, m) => m.validate() && acc,
      true,
    );
  }

  submit(language: Language) {
    if (this.validate()) {
      this.isSending = true;
      this.error = undefined;
      this.params.dispatch(this.prepareData(language));
    } else {
      this.error = { key: "general-validation-error" };
    }
  }

  cancel() {
    if (!this.isSending) {
      this.params.closeDialog();
    }
  }

  getRoleLabel(r: CompanyRole) {
    switch (r.role) {
      case RoleType.CHAIRPERSON:
      case RoleType.CEO:
      case RoleType.DEPUTY_CHAIRPERSON:
      case RoleType.DEPUTY_MEMBER:
      case RoleType.OBSERVER:
        return getRoleTypeLabel(r.role);
    }
  }
}

export type ResendDocumentForSigningData = {
  name: string;
  email: string;
  language: Language;
  signingMethod: SigningMethod;
};

export type ViewDocumentSigningStatusDialogModelParams = {
  documentId?: string;
  documentType: DocumentType | ArchiveDocumentType;
  title: TMessage;
  getSigningStatus(): void;
  downloadSignedDocument?(): void;
  resendDocumentForSigning?(signedDocumentId: string, data: ResendDocumentForSigningData): void;
  closeDialog: () => void;
  confirmDeleteSignatureRequest?: () => void;
};

export class ViewDocumentSigningStatusDialogModel {
  readonly params: ViewDocumentSigningStatusDialogModelParams;
  documentSigningStatus: Result<GetDocumentSigningStatusResponse, TMessage> = pending();
  alertMessage?: {
    type: "success" | "error";
    message: TMessage;
  };
  resentSignedDocumentIds: string[] = [];

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "delete-signature-request"; isLoading: boolean }
    | { type: "resending"; signedDocumentId: string }
    | { type: "edit"; signedDocumentId: string; model: SignerModel; isSending?: boolean } = {
    type: "ready",
  };

  constructor(params: ViewDocumentSigningStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init() {
    this.params.getSigningStatus();
  }

  get confirmCancelSignatureRequest() {
    return this.params.confirmDeleteSignatureRequest;
  }

  get hideCancelSignatureRequestButton() {
    return (
      isOk(this.documentSigningStatus) &&
      this.documentSigningStatus.data.documentSigningStatus !==
        DocumentSigningStatus.SIGNING_COMPLETE
    );
  }

  setSigningStatusResult(result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>) {
    this.setAlertMessage(result);
    this.documentSigningStatus = result;
    this.state = { type: "ready" };
  }

  setAlertMessage(result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>) {
    if (this.state.type === "resending") {
      if (isOk(result)) {
        const signer = this.signerBySignedDocumentId(this.state.signedDocumentId);
        this.resentSignedDocumentIds.push(this.state.signedDocumentId);
        this.alertMessage = {
          type: "success",
          message: {
            key: "dbot.resendDocumentForSigingSuccess",
            params: {
              documentType: this.title,
              name: signer?.name,
              email: signer?.email,
            },
          },
        };
      } else if (isFailure(result)) {
        this.alertMessage = { type: "error", message: result.error };
      }
    }
  }

  signerBySignedDocumentId(signedDocumentId: string): SigningStatusForSigner | undefined {
    return isOk(this.documentSigningStatus)
      ? this.documentSigningStatus.data.signerStatus.find(
          (d) => d.signedDocumentId === signedDocumentId,
        )
      : undefined;
  }

  get groupedSigners(): Array<[orgNo: string, SigningStatusForSigner[]]> {
    return isOk(this.documentSigningStatus)
      ? Object.entries(
          groupBy(this.documentSigningStatus.data.signerStatus, (s) => s.companyName ?? ""),
        ).map(([orgNo, signers]) => [orgNo, sortBy(signers, "name")])
      : [];
  }

  showGroupHeading(companyName: string): boolean {
    return Boolean(companyName) && this.groupedSigners.length > 1;
  }

  get canDownload(): boolean {
    return (
      this.params.downloadSignedDocument !== undefined &&
      isOk(this.documentSigningStatus) &&
      this.documentSigningStatus.data.signerStatus.every((s) => s.isSigned)
    );
  }

  download() {
    this.params.downloadSignedDocument?.();
    this.params.closeDialog();
  }

  reload() {
    this.params.getSigningStatus();
    this.state = { type: "reloading" };
  }

  get title() {
    return this.params.title;
  }

  get showSubmitButton(): boolean {
    return (
      (isOk(this.documentSigningStatus) &&
        this.params.documentType !== DocumentType.subscription_form) ||
      (isOk(this.documentSigningStatus) &&
        this.params.documentType === DocumentType.subscription_form &&
        !this.canDownload)
    );
  }

  get isSubmitDisabled() {
    return this.state.type !== "ready";
  }

  get isSubmitting(): boolean {
    return this.state.type === "reloading";
  }

  get submit() {
    if (this.canDownload) {
      return this.download;
    } else {
      return this.reload;
    }
  }

  get submitLabel(): TMessage | undefined {
    if (this.canDownload) {
      return { key: "see_signed" };
    } else {
      return { key: "signing.refresh" };
    }
  }

  get showCloseButton(): boolean {
    return !isPending(this.documentSigningStatus);
  }

  get isCloseDisabled() {
    return this.state.type !== "ready";
  }

  close() {
    this.params.closeDialog();
  }

  onClose() {
    this.state.type = "ready";
  }

  isResending(item: SigningStatusForSigner) {
    return this.state.type === "resending" && this.state.signedDocumentId === item.signedDocumentId;
  }

  isResentToSigner(item: SigningStatusForSigner) {
    return this.resentSignedDocumentIds.includes(item.signedDocumentId);
  }

  isResendDisabled(item: SigningStatusForSigner) {
    return this.state.type !== "ready" || this.isResentToSigner(item);
  }

  showResendButton(item: SigningStatusForSigner): boolean {
    return !item.isSigned && this.params.resendDocumentForSigning !== undefined;
  }

  openConfirmDeleteSigningRequestDialog() {
    this.state.type = "delete-signature-request";
  }

  setReopenResult(result: Result) {
    if (isOk(result)) {
      this.onClose();
    } else if (isFailure(result)) {
      this.alertMessage = { type: "error", message: { key: "general-error" } };
    }
  }

  get showCopyLinkButton(): boolean {
    return (
      !isOk(this.documentSigningStatus) ||
      this.documentSigningStatus?.data.documentSigningStatus !==
        DocumentSigningStatus.SIGNING_COMPLETE
    );
  }

  resend(item: SigningStatusForSigner) {
    if (this.state.type !== "ready") {
      return;
    }
    this.state = { type: "resending", signedDocumentId: item.signedDocumentId };
    this.params.resendDocumentForSigning?.(item.signedDocumentId, {
      name: item.name,
      email: item.email,
      language: item.language ?? DEFAULT_LANG,
      signingMethod: item.signingMethod,
    });
  }

  copySigningLinkToClipboard(signer: SigningStatusForSigner) {
    if (signer.signingUrl) {
      navigator.clipboard
        .writeText(signer.signingUrl)
        .then(() => {
          this.alertMessage = {
            type: "success",
            message: { key: "signing.link.success" },
          };
        })
        .catch((err) => {
          logError(err, "Unable to copy the recipient's document signing link");
          this.alertMessage = {
            type: "error",
            message: { key: "general-error" },
          };
        });
    }
  }

  tooltipLabel(signer: SigningStatusForSigner): TMessage {
    return signer.signingMethod === SigningMethod.HANDWRITING
      ? { key: "signing.link.tooltip.handwriting" }
      : { key: "signing.link.tooltip", params: { name: signer.name } };
  }

  isEditDisabled() {
    return this.state.type !== "ready";
  }

  showEditButton(item: SigningStatusForSigner) {
    return !item.isSigned && this.params.resendDocumentForSigning !== undefined;
  }

  edit(item: SigningStatusForSigner) {
    this.state = {
      type: "edit",
      signedDocumentId: item.signedDocumentId,
      model: new SignerModel({
        ...item,
        id: item.signedDocumentId,
        language: item.language ?? DEFAULT_LANG,
      }),
    };
  }

  showCancelEditButton() {
    return !this.isSendingEditedSigner();
  }

  cancelEdit() {
    this.state = { type: "ready" };
  }

  sendEditedSigner() {
    if (
      this.state.type !== "edit" ||
      this.state.isSending ||
      !this.state.model.validate() ||
      this.params.resendDocumentForSigning === undefined
    ) {
      return;
    }
    this.state.isSending = true;
    this.params.resendDocumentForSigning(this.state.signedDocumentId, {
      name: this.state.model.nameField.validatedValue,
      email: this.state.model.emailField.validatedValue!,
      language: this.state.model.languageField.value,
      signingMethod: this.state.model.methodField.value,
    });
  }

  isSendingEditedSigner() {
    return this.state.type === "edit" && this.state.isSending;
  }

  clearAlertMessage() {
    this.alertMessage = undefined;
  }
}

export type ViewSubscriptionStatusDialogModelParams = {
  documentId: string;
  title: TMessage;
  getSubscriptionStatus(): void;
  closeDialog: () => void;
  shareTypes: ShareType[];
};

export class ViewSubscriptionStatusDialogModel {
  readonly params: ViewSubscriptionStatusDialogModelParams;
  subscriptionStatus: Result<GetSubscriptionStatusResponse, TMessage> = pending();

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "resending"; signedDocumentId: string }
    | { type: "edit"; signedDocumentId: string; model: SignerModel; isSending?: boolean } = {
    type: "ready",
  };

  constructor(params: ViewSubscriptionStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init() {
    this.params.getSubscriptionStatus();
  }

  setSubscriptionStatusResult(result: Ok<GetSubscriptionStatusResponse> | Failure<TMessage>) {
    this.subscriptionStatus = result;
    this.state = { type: "ready" };
  }

  get subscriptions(): SubscriptionStatus[] {
    return isOk(this.subscriptionStatus)
      ? sortBy(this.subscriptionStatus.data.subscriptions, "name")
      : [];
  }

  reload() {
    this.params.getSubscriptionStatus();
    this.state = { type: "reloading" };
  }

  get showSubmitButton(): boolean {
    return isOk(this.subscriptionStatus);
  }

  get isSubmitDisabled() {
    return this.state.type !== "ready";
  }

  get isSubmitting(): boolean {
    return this.state.type === "reloading";
  }

  get submit() {
    return this.reload;
  }

  get submitLabel(): TMessage | undefined {
    return { key: "signing.refresh" };
  }

  get showCloseButton(): boolean {
    return !isPending(this.subscriptionStatus);
  }

  get isCloseDisabled() {
    return this.state.type !== "ready";
  }

  close() {
    this.params.closeDialog();
  }

  get title() {
    return this.params.title;
  }

  getShareType(shareTypeId: number) {
    return this.params.shareTypes.find((shareType) => shareType.sharetypeId === shareTypeId);
  }
}

type ViewDocumentViewingStatusDialogModelParams = {
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
};

export class RecipientViewModel {
  emailField: StringFieldModel;
  companyroleId?: CompanyroleId;
  boardMeetingId?: string;
  shareholderId?: number;
  recipient: DocumentViewingStatus;

  constructor(recipient: DocumentViewingStatus) {
    this.recipient = recipient;
    this.emailField = createEmailField({ value: recipient.recipientEmail, required: true });
    this.companyroleId = recipient.companyroleId;
    this.boardMeetingId = recipient.boardMeetingId;
    this.shareholderId = recipient.shareholderId;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get id(): string | number | undefined {
    return this.recipient.recipientDocumentId;
  }

  get isValid(): boolean {
    return this.emailField.isValid;
  }

  get name(): string {
    return this.recipient.recipientName;
  }

  validate() {
    this.emailField.validate();
    return this.isValid;
  }

  prepareUpdatedRecipientData(): ResendDocumentForViewing {
    return {
      recipientName: this.name,
      recipientEmail: this.emailField.validatedValue!,
      companyroleId: this.companyroleId,
      boardMeetingId: this.boardMeetingId,
      shareholderId: this.shareholderId,
      recipientDocumentId: this.recipient.recipientDocumentId,
    };
  }
}

export class ViewDocumentViewingStatusDialogModel {
  params: ViewDocumentViewingStatusDialogModelParams;
  status: Result<GetDocumentViewingStatusResponse, TMessage> = pending();
  isReloading = false;
  alertMessage?: {
    type: "success" | "error";
    message: TMessage;
  };

  constructor(params: ViewDocumentViewingStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "resending"; viewDocumentId: string }
    | { type: "edit"; viewDocumentId: string; model: RecipientViewModel; isSending?: boolean } = {
    type: "ready",
  };

  get recipientView(): DocumentViewingStatus[] {
    return isOk(this.status) ? sortBy(this.status.data.statusForViewers, "recipientName") : [];
  }

  init() {
    this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
  }

  setState(state: ViewDocumentViewingStatusDialogModel["state"]) {
    this.alertMessage = undefined;
    this.state = state;
  }

  cancelEdit() {
    this.setState({ type: "ready" });
  }

  isSendingEditedRecipient() {
    return this.state.type === "edit" && this.state.isSending;
  }

  sendUpdatedRecipient() {
    if (this.state.type !== "edit" || this.state.isSending || !this.state.model.validate()) {
      return;
    }
    this.state.isSending = true;
    this.params.dispatch({
      type: "dbot/resend-notice-to-updated-recipient",
      documentId: this.params.document.documentId,
      data: this.state.model.prepareUpdatedRecipientData(),
    });
  }

  showCancelEditButton() {
    return !this.isSendingEditedRecipient();
  }

  isEditDisabled() {
    return this.state.type !== "ready";
  }

  edit(recipient: DocumentViewingStatus) {
    this.setState({
      type: "edit",
      viewDocumentId: recipient.recipientDocumentId,
      model: new RecipientViewModel({
        ...recipient,
        shareholderId: recipient.shareholderId,
        companyroleId: recipient.companyroleId,
        boardMeetingId: recipient.boardMeetingId,
      }),
    });
  }

  isResending(resend: DocumentViewingStatus) {
    return (
      this.state.type === "resending" && this.state.viewDocumentId === resend.recipientDocumentId
    );
  }

  isResendDisabled() {
    return this.state.type !== "ready";
  }

  resend(data: DocumentViewingStatus) {
    if (this.state.type !== "ready") {
      return;
    }
    this.setState({ type: "resending", viewDocumentId: this.params.document.documentId });
    this.params.dispatch({
      type: "dbot/resend-notice-to-updated-recipient",
      documentId: this.params.document.documentId,
      data,
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/get-viewing-status-result":
        this.status = event.result;
        this.isReloading = false;
        switch (this.state.type) {
          case "resending":
            this.alertMessage = {
              type: "success",
              message: { key: "dbot.resendDocumentForUpdatingSuccess" },
            };
            this.state = { type: "ready" };
            break;
          case "edit":
            if (this.state.isSending) {
              this.alertMessage = {
                type: "success",
                message: { key: "dbot.resendDocumentForUpdatingSuccess" },
              };
              this.state = { type: "ready" };
            }
        }
        break;
      case "dbot/resend-notice-to-updated-recipient-result":
        if (isFailure(event.result)) {
          this.state = { type: "ready" };
          this.alertMessage = { type: "error", message: event.result.error };
        } else {
          this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        }
        break;
    }
  }

  get showReloadButton() {
    return !isPending(this.status);
  }

  reload() {
    this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
    this.isReloading = true;
  }

  get title() {
    return this.params.document.title;
  }

  close() {
    this.params.closeDialog();
  }

  clearAlertMessage() {
    this.alertMessage = undefined;
  }
}

// Keep in step with SigningTitle.incorporationSigningTitle, which names the same document on the
// signing page the signer reaches from this email.
export function signingEmailDocumentName(
  document: DocumentItem,
  decisions: DecisionItem[],
): TMessage {
  if (!isIncorporationDocument(document)) {
    return document.title;
  }
  const incorporation = decisions.find((d) => d.type === DecisionType.incorporation)?.decisionData;
  const companyName =
    incorporation?.type === DecisionType.incorporation
      ? incorporation.companyNames.find(isNotBlank)?.trim()
      : undefined;
  return companyName === undefined
    ? document.title
    : {
        key: "dbot.send-for-signing.incorporation-file-name",
        params: { documentTitle: document.title, companyName },
      };
}

export function signingEmailBody(
  document: DocumentItem,
  incorporationDocumentPart: IncorporationDocumentPart | undefined,
  senderName: string,
  decisions: DecisionItem[],
): TMessage {
  if (incorporationDocumentPart === "CONFIRMATION_OF_PAID_SHARE_CAPITAL_CONTRIBUTION") {
    return {
      key: "dbot.send-for-signing.paid-share-capital.email-body",
      params: { senderName },
    };
  }
  return {
    key: "dbot.send-for-signing.email-body",
    params: { senderName, fileName: signingEmailDocumentName(document, decisions) },
  };
}

// The BankID documents are generated from the incorporation document, so sending it for signing
// again deletes them permanently and creates them anew — see
// CustomDocumentService.createAdditionalCustomDocsIfIncorporation.
export function regeneratedIncorporationDocuments(
  reopenedDocumentId: string,
  documents: DocumentItem[],
  documentStatuses: ProjectDocumentStatus[],
): TMessage[] {
  const reopened = documents.find((d) => d.documentId === reopenedDocumentId);
  if (reopened === undefined || !isIncorporationDocument(reopened)) {
    return [];
  }
  const partDocumentIds = new Set(
    documentStatuses
      .filter((status) => status.incorporationDocumentPart !== undefined)
      .map((status) => status.documentId),
  );
  return documents.filter((d) => partDocumentIds.has(d.documentId)).map((d) => d.title);
}

export type ReopenLockedDocumentModelParams = {
  onSubmit: () => void;
  onCancel: () => void;
  canReopenDocument: boolean;
  regeneratedDocuments: TMessage[];
};

export class ReopenLockedDocumentDialogModel {
  onSubmit: () => void;
  onCancel: () => void;
  reopenResult?: Result;
  canReopenDocument: boolean;
  regeneratedDocuments: TMessage[];

  constructor(params: ReopenLockedDocumentModelParams) {
    this.onSubmit = () => {
      this.reopenResult = pending();
      params.onSubmit();
    };
    this.onCancel = params.onCancel;
    this.canReopenDocument = params.canReopenDocument;
    this.regeneratedDocuments = params.regeneratedDocuments;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isLoading() {
    return isPending(this.reopenResult);
  }

  setReopenResult(result: Result) {
    this.reopenResult = result;
  }
}

export type DynamicRecipientOption = {
  type: DynamicRecipientType;
  count: number;
};

export class RecipientsEditModel {
  recipientFields: RecipientFieldModel[];
  dynamicRecipients?: DynamicRecipient[];
  error?: TMessage;
  validationError?: TMessage;

  constructor(
    recipients: Recipient[],
    dynamicRecipients?: DynamicRecipient[],
    readonly onChange?: (type: "change" | "add" | "remove", m: RecipientFieldModel) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dynamicRecipients = dynamicRecipients;
    this.recipientFields = recipients.map(
      (r) => new RecipientFieldModel(r, (m) => onChange?.("change", m)),
    );
  }

  validate(): boolean {
    this.validationError = undefined;
    this.recipientFields.forEach((r) => r.validate());
    if (this.isEmpty) {
      this.validationError = { key: "recipients-model.validation.recipients-missing" };
    }
    return this.isValid;
  }

  get count() {
    return this.recipientFields.length;
  }

  get isEmpty() {
    return this.recipientFields.length <= 0 && (this.dynamicRecipients?.length ?? 0) <= 0;
  }

  get isValid(): boolean {
    return !this.validationError && this.recipientFields.every((r) => r.isValid);
  }

  get isChanged(): boolean {
    return this.recipientFields.some((r) => r.isChanged);
  }

  canEditRecipientName(r: RecipientFieldModel): boolean {
    return !r.ref;
  }

  getChangedRecipients(): Recipient[] {
    return this.recipientFields.filter((r) => r.isChanged).map((r) => r.toRecipient());
  }

  hasRecipient(r: Recipient): boolean {
    return this.recipientFields.some((x) => x.id === r.id || (!!r.ref && r.ref === x.ref));
  }

  addRecipient(r: Recipient) {
    if (!this.hasRecipient(r)) {
      const m = new RecipientFieldModel(r, (m) => this.onChange?.("change", m));
      this.recipientFields.push(m);
      this.onChange?.("add", m);
    }
  }

  removeRecipient(id: RecipientId) {
    const m = this.recipientFields.find((r) => r.id === id);
    if (m) {
      this.recipientFields.splice(this.recipientFields.indexOf(m), 1);
      this.onChange?.("remove", m);
    }
  }

  toRecipients(orgNo?: string): Recipient[] {
    return this.recipientFields.map((r) => r.toRecipient(orgNo));
  }

  addDynamicRecipient(r: DynamicRecipient) {
    this.dynamicRecipients = [...(this.dynamicRecipients ?? []), r];
  }

  removeDynamicRecipient(r: DynamicRecipientType) {
    this.dynamicRecipients = this.dynamicRecipients?.filter((x) => x.type !== r);
  }

  toDynamicRecipients(): DynamicRecipient[] | undefined {
    return this.dynamicRecipients;
  }

  hasDynamicRecipient(r: DynamicRecipientType) {
    return this.dynamicRecipients?.some((x) => x.type === r);
  }
}

export class RecipientFieldModel {
  nameField: StringFieldModel;
  emailField: StringFieldModel;

  constructor(
    readonly recipient: Recipient,
    readonly onChange?: (r: RecipientFieldModel) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.nameField = new StringFieldModel({
      value: recipient.name,
      onChange: () => onChange?.(this),
    });
    this.emailField = createEmailField({
      value: recipient.email,
      required: true,
      onChange: () => onChange?.(this),
    });
  }

  get id(): RecipientId {
    return this.recipient.id;
  }

  get ref(): string | undefined {
    return this.recipient.ref;
  }

  get isValid() {
    return this.nameField.isValid && this.emailField.isValid;
  }

  get isChanged() {
    return this.nameField.isChanged || this.emailField.isChanged;
  }

  validate(): boolean {
    this.nameField.validate();
    this.emailField.validate();
    return this.isValid;
  }

  toRecipient(orgNo?: string): Recipient {
    return {
      id: this.recipient.id,
      name: this.nameField.validatedValue,
      email: this.emailField.validatedValue ?? "",
      att: this.recipient.att,
      ref: this.recipient.ref,
      orgNo: orgNo ?? this.recipient.orgNo,
      roleType: this.recipient.roleType,
    };
  }

  rebase() {
    this.nameField.rebase();
    this.emailField.rebase();
  }
}

///
/// Actions
///

export type DecisionAction =
  | ApplyDecisionAction
  | ConfirmSendToBRAction
  | ConfirmSkipSendToBRAction
  | UploadAttachmentsEvent
  | DeleteAttachmentEvent
  | DeleteAttachmentResultEvent
  | DownloadAttachmentEvent
  | OpenIncreaseOfShareCapitalSubscribersStatusEvent
  | OpenIncreaseOfShareCapitalSubscribersAllocationEvent;

export type DocumentAction =
  | DownloadDocumentAction
  | DownloadSubscriptionFormsAction
  | OpenSubscriptionFormBatchPreviewDialogAction
  | OpenReviewDialogAction
  | OpenSigningStatusDialogAction
  | OpenViewingStatusDialogAction
  | OpenSendForReviewDialogAction
  | RefreshSigningStatusesAction
  | ValidateProjectAction
  | AutosignDocumentEvent
  | UnlockDocumentAction
  | OpenSubscriptionStatusEvent;

export const defaultReviewDeadlineDays = 7;

export function sendBoardMinutesForReviewTemplate({
  locale,
  user,
}: {
  user: User;
  locale: Language;
}): SendForReviewMessageTemplate {
  return {
    subject: { key: "dbot.send-for-review.board-minutes.message-subject" },
    body: {
      key: "dbot.send-for-review.board-minutes.message-body",
      params: {
        deadline: formatDate(locale, plusDays(Date.now(), defaultReviewDeadlineDays)),
        senderName: getFullName(user),
      },
    },
    linkText: { key: "dbot.send-for-review.board-minutes.link-text" },
    senderName: getFullName(user),
  };
}

export function openSendForReviewDialogAction(
  {
    document,
    signingStatus = DocumentSigningStatus.UNKNOWN,
    recipients,
  }: {
    document: DocumentItem;
    signingStatus?: DocumentSigningStatus;
    recipients?: CompanyRoleIdWithName[];
  },
  context: { user: User; locale: Language },
): OpenSendForReviewDialogAction | undefined {
  if (
    signingStatus === DocumentSigningStatus.UNKNOWN ||
    signingStatus === DocumentSigningStatus.SENT_FOR_REVIEW ||
    signingStatus === DocumentSigningStatus.SENT_FOR_VOTING
  ) {
    switch (document.type) {
      case DocumentType.board_meeting:
        return {
          type: "dbot/open-send-for-review-dialog",
          document,
          recipients,
          label: { key: "dbot.send-for-review" },
          messageTemplate: sendBoardMinutesForReviewTemplate(context),
        };
    }
  }
}

export function downloadSignedDocumentAction({
  document,
  signingStatus = DocumentSigningStatus.UNKNOWN,
  numberOfSignedDocuments,
}: {
  document: DocumentItem;
  signingStatus?: DocumentSigningStatus;
  numberOfSignedDocuments?: number;
}): DocumentAction | null {
  if (numberOfSignedDocuments !== undefined && numberOfSignedDocuments > 1) {
    return {
      document,
      type: "dbot/download-document",
      label: {
        key: "see_signed_documents",
        params: { n: numberOfSignedDocuments ?? 0 },
      },
    };
  } else if (
    numberOfSignedDocuments === 1 ||
    signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
  ) {
    return { document, type: "dbot/download-document", label: { key: "see_signed" } };
  }
  return null;
}

export function downloadSignedSubscriptionFormsAction({
  document,
  numberOfSignedDocuments,
  isSentForSigning = false,
}: {
  document: DocumentItem;
  numberOfSignedDocuments?: number;
  isSentForSigning?: boolean;
}): DocumentAction | null {
  if (isSentForSigning) {
    return {
      document,
      type: "dbot/download-subscription-forms",
      label: {
        key: "see_signed_documents",
        params: { n: numberOfSignedDocuments ?? 0 },
      },
    };
  }
  return null;
}

export function openSubscriptionFormBatchPreviewDialogAction({
  document,
  filePreview,
  isSentForSigning = false,
}: {
  document: DocumentItem;
  filePreview: (file: FileToPreview) => FilePreviewProps;
  isSentForSigning?: boolean;
}): DocumentAction | null {
  if (!isSentForSigning) {
    return {
      type: "dbot/openSubscriptionFormBatchPreviewDialog",
      label: { key: "preview" },
      title: document.title,
      filePreview,
    };
  }
  return null;
}

const reviewStatuses = [
  DocumentSigningStatus.SENT_FOR_REVIEW,
  DocumentSigningStatus.SENT_FOR_VOTING,
];

const followUpLockedBRStatuses: BR_DECISION_STATUS[] = [
  "SENT_TO_BR",
  "SENT_FOR_SIGNING",
  "PROCESSING",
  "BR_APPROVED",
];

const statusesAllowingDocumentToBeSentForSigning = [
  DocumentSigningStatus.UNKNOWN,
  ...reviewStatuses,
];

export function openSendForSigningDialogAction({
  document,
  showUnsupportedAttachmentWarning,
  signingStatus = DocumentSigningStatus.UNKNOWN,
  lockedSigningMethod,
  preselectDestinationFolder,
  attachments,
}: {
  document: DocumentItem;
  signingStatus?: DocumentSigningStatus;
  showUnsupportedAttachmentWarning: boolean;
  lockedSigningMethod?: SigningMethod;
  preselectDestinationFolder?: (
    destinationFolders: DestinationFolder[],
  ) => DestinationFolder | undefined;
  attachments?: UploadedAttachment[];
}): DocumentAction | null {
  if (statusesAllowingDocumentToBeSentForSigning.includes(signingStatus)) {
    return {
      type: "dbot/validate-project-action",
      contextDocumentId: document.documentId,
      label: { key: "send-for-signing" },
      successAction: {
        type: "dbot/fetch-document-signers",
        documentId: document.documentId,
        onSuccess: (data: GetProjectDocumentSignersResponse) => {
          const reviewFlow: DbotEvent[] = [
            {
              type: "dbot/fetch-destination-folders",
              onSuccess: (res) => [
                {
                  type: "dbot/open-review-dialog",
                  document,
                  signers: data.signers,
                  documents: data.documents,
                  companies: data.companies,
                  label: { key: "send-for-signing" },
                  showUnsupportedAttachmentWarning,
                  lockedSigningMethod,
                  destinationFolders: res.entries,
                  preselectedDestinationFolder: preselectDestinationFolder?.(res.entries),
                  attachments,
                },
              ],
              onFailure: [showGeneralError()],
            },
          ];
          // Batch projects use the per-company dialog; the package dialog would flatten companies.
          const isBatchProject = data.companies.length > 1;
          // Locked documents (e.g. BankID incorporation) aren't bundleable — stay on single-doc flow.
          const isLocked = lockedSigningMethod !== undefined;
          if (isBatchProject || isLocked) {
            return reviewFlow;
          }
          return [
            {
              type: "dbot/fetch-signable-documents",
              onSuccess: (signable) => {
                const documents = buildBundledDocuments(document, signable);
                if (documents.length <= 1) {
                  return reviewFlow;
                }
                return [
                  {
                    type: "dbot/open-bundle-selection-dialog",
                    currentDocument: document,
                    documents: documents,
                    companies: data.companies,
                  },
                ];
              },
              onFailure: reviewFlow,
            },
          ];
        },
        onFailure: [
          {
            type: "show-alert",
            messageType: "error",
            message: { key: "general-error" },
          },
        ],
      },
    };
  }
  return null;
}

function tagSignersWithDocument(
  signers: Signer[],
  documentId: string,
  previews: ProjectDocumentPreview[],
): Signer[] {
  return signers.map((s) => {
    const preview = previewForSigner(s, previews);
    return {
      ...s,
      documentId,
      documentTitle: preview?.documentTitle ?? "",
      documentGroupId: compact([documentId, preview?.reference]).join("/"),
    };
  });
}

function previewForSigner(
  signer: Signer,
  previews: ProjectDocumentPreview[],
): ProjectDocumentPreview | undefined {
  return (
    previews.find(
      (p) =>
        p.reference !== undefined && p.reference === signer.reference && p.orgNo === signer.orgNo,
    ) ?? previews[0]
  );
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

function toRecipientEmailSet(emails: string[]): Set<string> {
  return new Set(emails.map(normalizeEmail).filter((e) => e !== ""));
}

export function buildBundledDocuments(
  document: DocumentItem,
  signableResponse: GetSignableDocumentsResponse,
): BundledDocument[] {
  const current = signableResponse.documents.find((d) => d.documentId === document.documentId);
  if (!current) {
    return [];
  }
  const currentEmails = toRecipientEmailSet(current.recipients.map((r) => r.email));
  const others = signableResponse.documents.filter(
    (d) =>
      d.documentId !== document.documentId &&
      d.recipients.some((r) => currentEmails.has(normalizeEmail(r.email))),
  );
  if (others.length === 0) {
    return [];
  }
  return [current, ...others].map((d) => {
    const title = d.previews[0]?.documentTitle ?? "";
    return {
      documentId: d.documentId,
      documentType: d.documentType,
      title,
      signers: tagSignersWithDocument(d.recipients, d.documentId, d.previews),
      previews: d.previews,
      hasUnsupportedAttachments: d.hasUnsupportedAttachments,
      attachments: d.attachments,
      isCurrent: d.documentId === document.documentId,
    };
  });
}

export function openSigningStatusDialogAction(
  documentItem: DocumentItem,
  documentStatus?: ProjectDocumentStatus | DocumentPreview,
): DocumentAction | null {
  if (
    documentStatus?.signingStatus !== DocumentSigningStatus.UNKNOWN &&
    documentStatus?.signingStatus !== DocumentSigningStatus.SIGNING_COMPLETE
  ) {
    return {
      document: documentItem,
      type: "dbot/open-signing-status-dialog",
      label: {
        key: "n-of-m-have-signed",
        params: {
          n: documentStatus?.numberOfSignatures,
          m: documentStatus?.numberOfSigners,
        },
      },
    };
  }
  return null;
}

export function openSendNoticeDialogAction({
  document,
  isSendingAvailable,
  isBatchProject,
  isSendToNewParticipants,
  newRecipients,
  authMethod,
  meetingPassword,
}: {
  document: DocumentItem;
  isSendingAvailable: boolean;
  isBatchProject: boolean;
  isSendToNewParticipants?: boolean;
  newRecipients?: number;
  authMethod?: MeetingAuthMethod;
  meetingPassword?: string;
}): DocumentAction | null {
  if (isSendingAvailable && !isBatchProject) {
    return {
      type: "dbot/validate-project-action",
      contextDocumentId: document.documentId,
      label: isSendToNewParticipants
        ? { key: "dbot.send-notice.new-recipients", params: { count: newRecipients } }
        : { key: "dbot.send-notice" },
      successAction: {
        type: "dbot/open-review-dialog",
        document,
        label: document.title,
        authMethod,
        meetingPassword,
        isSendToNewParticipants,
      },
    };
  }
  return null;
}

export function disabledSendNoticeInfo({
  isBatchProject,
}: {
  isBatchProject: boolean;
}): TMessage | undefined {
  return isBatchProject ? { key: "dbot.batchProject.disabled-send-notice-info" } : undefined;
}

export function openNotifyNoticeChangeDialogAction({
  document,
  sendingTime,
}: {
  document: DocumentItem;
  sendingTime?: string;
}): DocumentAction | null {
  if (sendingTime && document.type === DocumentType.notice_board_meeting) {
    return {
      type: "dbot/validate-project-action",

      label: { key: "dbot.send-notice.notify" },
      successAction: {
        type: "dbot/open-review-dialog",
        document,
        label: { key: "dbot.send-for-viewing.board-meeting.resend.title" },
        emailBody: { key: "dbot.send-for-viewing.board-meeting.resend.email-body" },
        isResend: true,
      },
    };
  }
  return null;
}

export function openViewingStatusDialog(
  documentItem: DocumentItem,
  documentStatus?: ProjectDocumentStatus,
): DocumentAction | null {
  if (documentStatus?.sentAt) {
    return {
      document: documentItem,
      type: "dbot/open-viewing-status-dialog",
      label: {
        key: "n-of-m-have-seen",
        params: {
          n: documentStatus?.numberOfViewed,
          m: documentStatus?.numberOfViewers,
        },
      },
    };
  }
  return null;
}

export class DecisionListItem {
  orgNo: string;
  decision: DecisionItem;
  followUps: FollowUp[];
  eventLogs: EventLog[];
  isApplying: boolean;
  isSendingToBR: boolean;
  isEnabled: boolean;
  isPreviousDecisionApplied: boolean;
  uploadedDecisionAttachments: DecisionAttachment[];
  fileUploadConfig: FileUploadConfig;
  documents?: DocumentListItem[];
  roles: CompanyRole[];
  isCheckingIncorporationStatus: boolean;
  incorporationStatusResult?: Result<DseStatusResponse, TMessage>;
  activationInfo?: Result<IncorporationActivationInfo, TMessage>;
  activationSubscription?: IncorporationActivationSubscription;
  isCreatingMergerCounterpart: boolean;

  constructor(params: {
    orgNo: string;
    decision: DecisionItem;
    followUps: FollowUp[];
    eventLogs: EventLog[];
    isApplying: boolean;
    isSendingToBR: boolean;
    isEnabled: boolean;
    isPreviousDecisionApplied: boolean;
    uploadedDecisionAttachments: DecisionAttachment[];
    fileUploadConfig: FileUploadConfig;
    documents?: DocumentListItem[];
    roles: CompanyRole[];
    isCheckingIncorporationStatus: boolean;
    incorporationStatusResult?: Result<DseStatusResponse, TMessage>;
    activationInfo?: Result<IncorporationActivationInfo, TMessage>;
    activationSubscription?: IncorporationActivationSubscription;
    isCreatingMergerCounterpart?: boolean;
  }) {
    makeAutoObservable(this);
    this.orgNo = params.orgNo;
    this.decision = params.decision;
    this.followUps = params.followUps;
    this.eventLogs = params.eventLogs;
    this.isApplying = params.isApplying;
    this.isSendingToBR = params.isSendingToBR;
    this.isEnabled = params.isEnabled;
    this.isPreviousDecisionApplied = params.isPreviousDecisionApplied;
    this.uploadedDecisionAttachments = params.uploadedDecisionAttachments;
    this.fileUploadConfig = params.fileUploadConfig;
    this.documents = params.documents;
    this.roles = params.roles;
    this.isCheckingIncorporationStatus = params.isCheckingIncorporationStatus;
    this.incorporationStatusResult = params.incorporationStatusResult;
    this.activationInfo = params.activationInfo;
    this.activationSubscription = params.activationSubscription;
    this.isCreatingMergerCounterpart = params.isCreatingMergerCounterpart ?? false;
  }

  checkIncorporationStatusAction() {
    return checkIncorporationStatusEvent(this.decision.decisionId);
  }

  simulateBrResponseAction(kind: DseSimulatedBrResponseKind) {
    return simulateBrResponseEvent(this.decision.decisionId, kind);
  }

  fetchActivationInfoAction() {
    return fetchIncorporationActivationInfoEvent();
  }

  selectActivationSubscriptionAction(subscription: IncorporationActivationSubscription) {
    return selectIncorporationActivationSubscriptionEvent(this.decision.decisionId, subscription);
  }

  uploadNewDecisionAttachment(
    files: File[],
    dispatch: (action: DecisionAction) => void,
    attachmentType: AttachmentType,
    followUp: FollowUp,
  ) {
    if (files.length) {
      dispatch(
        uploadAttachmentsEvent({
          source: followUp,
          files,
          attachmentType,
          reference: this.decision.decisionId,
          onSuccess: [
            updateFollowUpStatusEvent(this.orgNo, this.decision.decisionId, followUp.data),
          ],
        }),
      );
    }
  }

  get applied() {
    return isDecisionApplied(this.followUps);
  }

  get supportsSendToBR() {
    return this.followUps.some((d) => d.data.type === FollowUpType.sendToBR);
  }

  get isFollowUpLocked() {
    return this.followUps.some(
      (d) =>
        d.data.type === FollowUpType.sendToBR &&
        !d.data.isRegistration &&
        followUpLockedBRStatuses.includes(d.data.brStatus),
    );
  }

  get sendToBRSigners() {
    const companyRoles = sortedBoardAndCEO(this.roles).filter(
      (a) => a.role !== RoleType.DEPUTY_MEMBER,
    );
    return mapToBRSigners(companyRoles);
  }

  get actions() {
    return {
      applyDecision: applyDecisionAction(this.orgNo, this.decision),
      confirmSendToBR: (sendToBRFollowUpData: SendToBRFollowUp) =>
        this.decision.type === DecisionType.incorporation
          ? confirmSendIncorporationToBRAction(this.orgNo, this.decision.decisionId)
          : confirmSendToBRAction(
              this.orgNo,
              this.decision.decisionId,
              sendToBRFollowUpData.isRegistration,
            ),
      confirmSkipSendToBR: confirmSkipSendToBRAction(this.orgNo, this.decision.decisionId),
    };
  }

  deleteAttachment(attachmentId: string, followUp: FollowUp): DbotEvent[] {
    return [
      deleteAttachmentEvent({
        attachmentId,
        source: followUp,
        reference: this.decision.decisionId,
        onSuccess: [updateFollowUpStatusEvent(this.orgNo, this.decision.decisionId, followUp.data)],
      }),
    ];
  }
}

export function unlockAction(documentId: string): DocumentAction {
  return {
    documentId,
    label: { key: "dbot.action.reopen" },
    type: "dbot/unlock-document",
  };
}

export function mapSignerToUpdateCompanyRoleItem(
  signer: Signer,
): UpdateCompanyRoleItem | undefined {
  const r = resolveUrnReference(signer.reference);
  switch (r?.type) {
    case "companyroleId":
      return { companyroleId: r.id, email: signer.email };
  }
}

export function hasInvalidRecipients(recipients: Recipient[]) {
  return !new RecipientsEditModel(recipients).validate();
}

export function getShareholderRecipients(
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): Recipient[] {
  return shareholdersWithSharesExcludingSelf(shareholders, sharesForShareholders)
    .filter(
      (shareholder) => shareholder.birthDateOrOrganisationNumber !== shareholder.organizationNumber,
    )
    .map((s) => ({
      id: s.id,
      name: s.name,
      email: s.email,
    }));
}

export function getBoardRecipients(context: ProjectContext): Recipient[] {
  return context.boardMeetingNoticeRecipients.map((r) => ({ ...r, email: r.email ?? "" }));
}

export function getBoardRecipientsEligibleForPowerOfAttorney(context: ProjectContext) {
  const roleTypesEligibleForReceivingPowerOfAttorney: Set<RoleType> = new Set([
    RoleType.CHAIRPERSON,
    RoleType.CEO,
  ]);
  return getBoardRecipientsWithRoleTypes(context, roleTypesEligibleForReceivingPowerOfAttorney);
}

export function getBoardRecipientsWithRoleTypes(
  context: ProjectContext,
  roleTypes: Set<RoleType>,
): Recipient[] {
  const roles = context.initialState.roles;
  return roles
    .filter((r) => roleTypes.has(r.role))
    .map((r) => ({
      id: r.companyroleId,
      name: getFullName(r),
      email: r.email ?? "",
    }));
}

export function getBoardSignersInfo(context: ProjectContext): TMessage | undefined {
  if (!supportsSelectionOfSigners(context.initialState.roles)) {
    return { key: "dbot.boardmeeting.all-must-sign-when-less-than-5" };
  }
}

export function getViewingStatusLabel(s: DocumentViewingStatus): TMessage {
  if (s.viewedTime) {
    return { key: "dbot.summary.viewStatus.viewed" };
  } else if (
    s.emailStatus === EmailStatus.SENDING_FAILED ||
    s.emailStatus === EmailStatus.DELIVERY_FAILED
  ) {
    return { key: "dbot.summary.viewStatus.sendingFailed" };
  }
  return { key: "dbot.summary.viewStatus.waiting" };
}

export function getViewingStatusColor(s: DocumentViewingStatus): "success" | "error" | "neutral" {
  if (s.viewedTime) {
    return "success";
  } else if (
    s.emailStatus === EmailStatus.SENDING_FAILED ||
    s.emailStatus === EmailStatus.DELIVERY_FAILED
  ) {
    return "error";
  }
  return "neutral";
}

export function getSigningStatusColor(s: SigningStatusForSigner): "success" | "error" | "neutral" {
  if (s.isSigned) {
    return "success";
  }
  switch (s.sendingStatus) {
    case EmailStatus.DELIVERY_FAILED:
    case EmailStatus.SENDING_FAILED:
      return "error";
    default:
      return "neutral";
  }
}

export type SendToBRMatter = {
  decisionId: string;
  isRegistration: boolean;
  title: TMessage;
  isReady: boolean;
  brStatus: SendToBRFollowUp["brStatus"];
  additionalSigners: AdditionalBRSigner[];
};

export type AdditionalBRSignerLine = {
  person: TMessage;
  companies?: TextWithTooltipProps;
};

function additionalBRSignerReasonLabel(reason: AdditionalBRSignerReason): TMessage {
  switch (reason) {
    case AdditionalBRSignerReason.NEW_TO_BOARD:
      return { key: "dbot.summary.sendToBR.additionalSigner.reason.NEW_TO_BOARD" };
    case AdditionalBRSignerReason.BOARD_ROLE_CHANGE:
      return { key: "dbot.summary.sendToBR.additionalSigner.reason.BOARD_ROLE_CHANGE" };
    case AdditionalBRSignerReason.NEW_MANAGING_DIRECTOR:
      return { key: "dbot.summary.sendToBR.additionalSigner.reason.NEW_MANAGING_DIRECTOR" };
    case AdditionalBRSignerReason.NEW_AUDITOR:
      return { key: "dbot.summary.sendToBR.additionalSigner.reason.NEW_AUDITOR" };
    case AdditionalBRSignerReason.AUDITOR:
      return { key: "dbot.summary.sendToBR.additionalSigner.reason.AUDITOR" };
    case AdditionalBRSignerReason.CONFIRMS_SHARE_CAPITAL_CONTRIBUTIONS:
      return {
        key: "dbot.summary.sendToBR.additionalSigner.reason.CONFIRMS_SHARE_CAPITAL_CONTRIBUTIONS",
      };
  }
}

type AdditionalBRSignerInCompany = {
  signer: AdditionalBRSigner;
  companyName: string;
};

function toAdditionalBRSignerLines(
  entries: AdditionalBRSignerInCompany[],
  showCompanies: boolean,
): AdditionalBRSignerLine[] {
  return Object.values(
    groupBy(entries, ({ signer }) => signer.name ?? signer.organisationNumber ?? signer.email),
  ).map((personEntries) => ({
    person: additionalBRSignerPerson(personEntries.map((entry) => entry.signer)),
    companies: showCompanies
      ? companiesLabel(uniq(personEntries.map((entry) => entry.companyName)))
      : undefined,
  }));
}

function additionalBRSignerPerson(signers: AdditionalBRSigner[]): TMessage {
  const name = signers[0].name ?? signers[0].organisationNumber ?? "";
  const email = signers.find((signer) => signer.email)?.email;
  const reasons: TMessageParam = {
    type: "capitalize",
    value: {
      type: "list",
      value: uniq(signers.map((signer) => signer.reason)).map(additionalBRSignerReasonLabel),
    },
  };
  return email
    ? { key: "dbot.summary.sendToBR.additionalSigner", params: { name, email, reasons } }
    : { key: "dbot.summary.sendToBR.additionalSigner.withoutEmail", params: { name, reasons } };
}

export function additionalBRSignerLines(signers: AdditionalBRSigner[]): AdditionalBRSignerLine[] {
  return toAdditionalBRSignerLines(
    signers.map((signer) => ({ signer, companyName: "" })),
    false,
  );
}

function companiesLabel(companyNames: string[]): TextWithTooltipProps {
  return companyNames.length === 1
    ? {
        text: {
          key: "dbot.summary.sendToBR.additionalSigner.company",
          params: { company: companyNames[0] },
        },
      }
    : {
        text: {
          key: "dbot.summary.sendToBR.additionalSigner.companyCount",
          params: { companyCount: companyNames.length },
        },
        tooltip: { type: "md-list", value: companyNames },
      };
}

export type CompanyBRContext = {
  orgNo: string;
  name: string;
  matters: SendToBRMatter[];
  signers: BRSigner[];
};

export type CompanySelection = "all" | "selected";
export type SignerAssignment = BRSignerRole.chairperson | BRSignerRole.ceo | "perCompany";
export type SignerCount = "single" | "multiple";

// One full loop of envelope-sending.svg. We let it complete a cycle before showing the sent state.
const ENVELOPE_SENDING_ANIMATION_CYCLE_MS = 2400;

function applyResultAfterSendingAnimation(
  result: Result<TMessage, TMessage>,
  submittedAt: number,
  setResult: (result: Result<TMessage, TMessage>) => void,
) {
  if (!isOk(result)) {
    setResult(result);
    return;
  }
  const remaining = Math.max(0, ENVELOPE_SENDING_ANIMATION_CYCLE_MS - (Date.now() - submittedAt));
  setTimeout(
    action(() => setResult(result)),
    remaining,
  );
}

export function getSendToBRSuccessMessage(
  matterCount: number,
  companyCount: number,
  companyName: string,
): TMessage {
  return {
    key: "dbot.summary.sendToBR.sent.title",
    params: {
      count: matterCount,
      companies:
        companyCount === 1
          ? { translated: companyName }
          : { key: "dbot.summary.sendToBR.sent.companyCount", params: { count: companyCount } },
    },
  };
}

export function brSignerRoleLabel(role: BRSignerRole): TMessage {
  switch (role) {
    case BRSignerRole.ceo:
      return { key: "roles.ceo" };
    case BRSignerRole.chairperson:
      return { key: "roles.chairperson" };
    case BRSignerRole.member:
      return { key: "roles.member" };
    case BRSignerRole.other:
      return { key: "sendToBr.specify" };
  }
}

export function selectedBRSignerLabel({ role, name, email }: BRSigner): TMessage {
  const params = { signer: { translated: email ? `${name} (${email})` : name } };
  switch (role) {
    case BRSignerRole.ceo:
      return { key: "dbot.summary.sendToBR.selected-signer.ceo", params };
    case BRSignerRole.chairperson:
      return { key: "dbot.summary.sendToBR.selected-signer.chairperson", params };
    case BRSignerRole.member:
      return { key: "dbot.summary.sendToBR.selected-signer.member", params };
    case BRSignerRole.other:
      return { key: "dbot.summary.sendToBR.selected-signer.other", params };
  }
}

export class ConfirmSendToBRDialogModel {
  onSubmit: (signers: Record<string, BRSigner[]>, decisions: SendToBRDecision[]) => void;
  onCancel: () => void;
  companySelectionModel: RadioFieldModel<CompanySelection>;
  mattersModel: OptionFieldModel<SendToBRMatter>;
  signerAssignmentModel: RadioFieldModel<SignerAssignment>;
  signerByOrgNo: Map<string, RadioFieldModel<BRSigner | undefined>>;
  signersByOrgNo: Map<string, OptionFieldModel<BRSigner>>;
  signerCountModel: RadioFieldModel<SignerCount>;
  soleSignerByOrgNo: Map<string, RadioFieldModel<BRSigner | undefined>>;
  result?: Result<TMessage, TMessage>;
  submittedAt = 0;

  constructor(
    readonly originOrgNo: string,
    readonly originDecisionId: string,
    readonly companies: CompanyBRContext[],
    onSubmit: (signers: Record<string, BRSigner[]>, decisions: SendToBRDecision[]) => void,
    onCancel: () => void,
  ) {
    this.onSubmit = onSubmit;
    this.onCancel = onCancel;

    const originCompany = companies.find((c) => c.orgNo === originOrgNo);
    const originMatter = originCompany?.matters.find((m) => m.decisionId === originDecisionId);
    const defaultToAllCompanies =
      this.submittableCompanies.length > 1 &&
      originMatter !== undefined &&
      !originMatter.isRegistration;

    this.companySelectionModel = new RadioFieldModel<CompanySelection>({
      idFn: identity,
      labelFn: (scope) =>
        scope === "all"
          ? {
              key: "dbot.summary.sendToBR.companySelection.all",
              params: { count: this.submittableCompanies.length },
            }
          : {
              key: "dbot.summary.sendToBR.companySelection.selected",
              params: { name: originCompany?.name ?? "" },
            },
      options: ["all", "selected"],
      value: defaultToAllCompanies ? "all" : "selected",
      required: true,
      onChange: () => {
        this.selectReadyMatters();
        this.resetSignerAssignment();
      },
    });

    this.mattersModel = new OptionFieldModel<SendToBRMatter>({
      options: () => this.availableMatters,
      idFn: (matter) => matter.decisionId,
      labelFn: (matter) => matter.title,
      disabledFn: (matter) => !this.isMatterReady(matter.decisionId),
      tooltipFn: (matter) => this.matterDisabledReason(matter.decisionId),
      required: true,
    });
    this.selectReadyMatters();

    this.signerByOrgNo = new Map(
      companies.map((c) => [
        c.orgNo,
        new RadioFieldModel<BRSigner | undefined>({
          idFn: (option) => option?.id?.toString() ?? "",
          labelFn: (option) =>
            option !== undefined ? { translated: option.name } : { translated: "" },
          options: c.signers,
          value: c.signers.find((s) => s.role === BRSignerRole.chairperson),
          required: true,
        }),
      ]),
    );

    this.signersByOrgNo = new Map(
      companies.map((c) => [
        c.orgNo,
        new OptionFieldModel<BRSigner>({
          idFn: brSignerId,
          labelFn: (option) => ({ translated: option.name }),
          options: c.signers,
          validator: (selected) =>
            selected.length === 0 ? { key: "dbot.summary.sendToBR.signers.required" } : undefined,
        }),
      ]),
    );

    this.signerCountModel = new RadioFieldModel<SignerCount>({
      idFn: identity,
      labelFn: (count) => ({ key: `dbot.summary.sendToBR.signerCount.${count}` }),
      options: ["single", "multiple"],
      value: "single",
      required: true,
    });

    this.soleSignerByOrgNo = new Map(
      companies.map((c) => [
        c.orgNo,
        new RadioFieldModel<BRSigner | undefined>({
          idFn: (option) => (option !== undefined ? brSignerId(option) : ""),
          labelFn: (option) => ({ translated: option?.name ?? "" }),
          options: c.signers,
          value: undefined,
          required: true,
        }),
      ]),
    );

    this.signerAssignmentModel = new RadioFieldModel<SignerAssignment>({
      idFn: (mode) => mode.toString(),
      labelFn: (mode) =>
        mode === "perCompany"
          ? { key: "dbot.summary.sendToBR.signing.perCompany" }
          : {
              key: "dbot.summary.sendToBR.signing.modeLabel",
              params: {
                role: brSignerRoleLabel(mode),
                description: this.signerAssignmentDescription(mode) ?? { translated: "" },
              },
            },
      disabledFn: (mode) => this.isSignerAssignmentDisabled(mode),
      options: [BRSignerRole.chairperson, BRSignerRole.ceo, "perCompany"],
      value: BRSignerRole.chairperson,
      required: true,
      onChange: (mode) => this.applySignerAssignment(mode),
    });

    makeAutoObservable(this);
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-to-br-result":
        applyResultAfterSendingAnimation(
          event.result,
          this.submittedAt,
          (result) => (this.result = result),
        );
        break;
    }
  }

  get submittableCompanies(): CompanyBRContext[] {
    return this.companies.filter((c) =>
      c.matters.some((m) => m.decisionId === this.originDecisionId && m.isReady),
    );
  }

  get targetCompanies(): CompanyBRContext[] {
    return this.companySelectionModel.value === "selected"
      ? this.companies.filter((c) => c.orgNo === this.originOrgNo)
      : this.submittableCompanies;
  }

  get hasMultipleTargetCompanies(): boolean {
    return this.targetCompanies.length > 1;
  }

  get showCompanySelection(): boolean {
    return this.submittableCompanies.length > 1;
  }

  get availableMatters(): SendToBRMatter[] {
    const matters = this.targetCompanies.flatMap((c) => c.matters);
    return sortBy(
      uniqBy(matters, (m) => m.decisionId),
      (m) => (m.decisionId === this.originDecisionId ? 0 : 1),
    );
  }

  isMatterReady(decisionId: string): boolean {
    return this.targetCompanies.every(
      (company) => company.matters.find((m) => m.decisionId === decisionId)?.isReady ?? false,
    );
  }

  matterDisabledReason(decisionId: string): TMessage | undefined {
    if (this.isMatterReady(decisionId)) {
      return undefined;
    }
    const isAlreadySubmitted = this.targetCompanies.some((c) => {
      const matter = c.matters.find((m) => m.decisionId === decisionId);
      return matter !== undefined && matter.brStatus !== "NOT_SENT";
    });
    return isAlreadySubmitted
      ? { key: "dbot.summary.sendToBR.matters.alreadySubmitted" }
      : { key: "dbot.summary.sendToBR.matters.notReady" };
  }

  selectReadyMatters() {
    this.mattersModel.setSelected(
      this.availableMatters.filter((m) => this.isMatterReady(m.decisionId)),
    );
  }

  get showMatters(): boolean {
    return this.availableMatters.length > 1;
  }

  get signerRows(): {
    company: CompanyBRContext;
    signerModel: RadioFieldModel<BRSigner | undefined>;
  }[] {
    return this.targetCompanies.map((company) => ({
      company,
      signerModel: this.signerByOrgNo.get(company.orgNo)!,
    }));
  }

  get isPerCompanyEditable(): boolean {
    return this.signerAssignmentModel.value === "perCompany";
  }

  roleAvailableInAllCompanies(role: BRSignerRole): boolean {
    return this.targetCompanies.every((c) => c.signers.some((s) => s.role === role));
  }

  signerAssignmentDescription(mode: SignerAssignment): TMessage | undefined {
    if (mode === "perCompany") {
      return undefined;
    }
    if (!this.roleAvailableInAllCompanies(mode)) {
      return { key: "dbot.summary.sendToBR.signing.incomplete" };
    }
    const distinct = new Set(
      this.targetCompanies.map((c) => c.signers.find((s) => s.role === mode)?.name),
    ).size;
    return { translated: `(${distinct})` };
  }

  isSignerAssignmentDisabled(mode: SignerAssignment): boolean {
    return mode !== "perCompany" && !this.roleAvailableInAllCompanies(mode);
  }

  applySignerAssignment(mode: SignerAssignment) {
    if (mode === "perCompany") {
      return;
    }
    for (const company of this.companies) {
      this.signerByOrgNo.get(company.orgNo)?.setValue(company.signers.find((s) => s.role === mode));
    }
  }

  resetSignerAssignment() {
    this.signerAssignmentModel.setValue(BRSignerRole.chairperson);
  }

  validateAndSubmit() {
    this.validate();
    if (this.isValid) {
      const signers = this.buildSigners();
      const decisions = this.mattersModel.selected
        .filter((matter) => this.isMatterReady(matter.decisionId))
        .map((matter) => ({
          decisionId: matter.decisionId,
          isRegistration: matter.isRegistration,
        }));
      this.result = pending();
      this.submittedAt = Date.now();
      this.onSubmit(signers, decisions);
    }
  }

  get additionalSigners(): AdditionalBRSignerLine[] {
    const selectedDecisionIds = new Set(this.mattersModel.selected.map((m) => m.decisionId));
    const entries = this.targetCompanies.flatMap((company) =>
      company.matters
        .filter((matter) => selectedDecisionIds.has(matter.decisionId))
        .flatMap((matter) => matter.additionalSigners)
        .map((signer) => ({ signer, companyName: company.name })),
    );
    return toAdditionalBRSignerLines(entries, this.hasMultipleTargetCompanies);
  }

  get singleCompanySigners(): {
    company: CompanyBRContext;
    signersModel: OptionFieldModel<BRSigner>;
    soleSignerModel: RadioFieldModel<BRSigner | undefined>;
  } {
    const company = this.targetCompanies[0];
    return {
      company,
      signersModel: this.signersByOrgNo.get(company.orgNo)!,
      soleSignerModel: this.soleSignerByOrgNo.get(company.orgNo)!,
    };
  }

  get isMultipleSigners(): boolean {
    return this.signerCountModel.value === "multiple";
  }

  validate() {
    this.companySelectionModel.validate();
    this.mattersModel.validate();
    if (this.hasMultipleTargetCompanies) {
      this.signerRows.forEach(({ signerModel }) => signerModel.validate());
    } else if (this.isMultipleSigners) {
      this.singleCompanySigners.signersModel.validate();
    } else {
      this.singleCompanySigners.soleSignerModel.validate();
    }
    return this.isValid;
  }

  get isValid(): boolean {
    return this.mattersModel.isValid && this.isSignersValid;
  }

  get isSignersValid(): boolean {
    if (this.hasMultipleTargetCompanies) {
      return this.signerRows.every(({ signerModel }) => signerModel.isValid);
    }
    const { signersModel, soleSignerModel } = this.singleCompanySigners;
    return this.isMultipleSigners ? signersModel.isValid : soleSignerModel.isValid;
  }

  buildSigners(): Record<string, BRSigner[]> {
    if (!this.hasMultipleTargetCompanies) {
      const { company, signersModel, soleSignerModel } = this.singleCompanySigners;
      const selected = this.isMultipleSigners
        ? signersModel.selected
        : [soleSignerModel.value].filter((s) => s !== undefined);
      return { [company.orgNo]: selected };
    }
    return Object.fromEntries(
      this.signerRows.flatMap(({ company, signerModel }) =>
        signerModel.value ? [[company.orgNo, [signerModel.value]]] : [],
      ),
    );
  }
}

export class ConfirmSendIncorporationToBRDialogModel {
  onSubmit: () => void;
  onCancel: () => void;
  isSendingEnabled: boolean;
  result?: Result<TMessage, TMessage>;
  submittedAt = 0;
  prekontrollResult: Result<undefined, TMessage> = pending();
  prekontrollErrors?: DsePrekontrollErrorItem[];

  constructor(onSubmit: () => void, onCancel: () => void, isSendingEnabled: boolean) {
    this.onSubmit = onSubmit;
    this.onCancel = onCancel;
    this.isSendingEnabled = isSendingEnabled;
    makeAutoObservable(this);
  }

  handleEvent(event: DbotEvent) {
    if (event.type === "dbot/send-to-br-result") {
      applyResultAfterSendingAnimation(
        event.result,
        this.submittedAt,
        (result) => (this.result = result),
      );
    }
    if (event.type === "dbot/prekontroll-incorporation-result") {
      this.prekontrollResult = event.result;
      this.prekontrollErrors = event.errors;
    }
  }

  get isPrekontrollPending(): boolean {
    return isPending(this.prekontrollResult);
  }

  get isPrekontrollPassed(): boolean {
    return isOk(this.prekontrollResult);
  }

  get prekontrollError(): TMessage | undefined {
    return isFailure(this.prekontrollResult) ? this.prekontrollResult.error : undefined;
  }

  get sendingDisabledReason(): TMessage | undefined {
    return this.isSendingEnabled
      ? undefined
      : { key: "dbot.summary.sendToBR.incorporation.open-beta" };
  }

  validateAndSubmit() {
    if (!this.isSendingEnabled || !this.isPrekontrollPassed) return;
    this.result = pending();
    this.submittedAt = Date.now();
    this.onSubmit();
  }
}

export function getSigningStatusLabel(item: SigningStatusForSigner): TMessage {
  if (item.isSigned) {
    return { key: "dbot.summary.signingStatus.signed" };
  }
  switch (item.sendingStatus) {
    case EmailStatus.UNSENT:
      return { key: "dbot.summary.signingStatus.not_sent" };
    case EmailStatus.SENT:
    case EmailStatus.DELIVERED:
      return { key: "dbot.summary.signingStatus.sent" };
    case EmailStatus.DELIVERY_FAILED:
      return { key: "dbot.summary.signingStatus.delivery_failed" };
    case EmailStatus.SENDING_FAILED:
      return { key: "dbot.summary.signingStatus.failed_to_send" };
    default:
      return { key: "dbot.summary.signingStatus.unknown" };
  }
}

export function isSendToBRReady(item: DecisionListItem, followUp: FollowUp): boolean {
  return item.isEnabled && followUp.isActive && followUp.status === FollowUpStatus.READY;
}

function getSendToBRHeading(item: DecisionListItem, data: SendToBRFollowUp): TMessage {
  return containsSendToBRRegistration(item.followUps)
    ? {
        key: data.isRegistration
          ? "dbot.summary.sendToBR.registration"
          : "dbot.summary.sendToBR.completion",
      }
    : { key: "dbot.summary.sendToBR" };
}

export function getSendToBRPanel(
  item: DecisionListItem,
  followUp: FollowUp,
  data: SendToBRFollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: data.isRegistration ? "register-to-br" : "send-to-br",
    followUp: {
      item: followUp,
      orgNo: item.orgNo,
      decisionId: item.decision.decisionId,
      isBRRegistration: data.isRegistration,
    },
    icon: !item.isEnabled
      ? "clock"
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? "arrows-clockwise"
        : followUp.status === FollowUpStatus.COMPLETED
          ? { name: "check", color: "success", weight: "bold" }
          : followUp.isActive
            ? { name: "arrow-right", color: "info" }
            : "clock",
    heading: getSendToBRHeading(item, data),
    label: !item.isEnabled
      ? { key: "dbot.summary.missing-system-settings" }
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.inProgress" }
        : data.brStatus === "DELETED"
          ? { key: "dbot.summary.sendToBR.skipped.label" }
          : followUp.status === FollowUpStatus.COMPLETED
            ? { key: "dbot.summary.completed" }
            : !followUp.isActive
              ? { key: "dbot.summary.pending-decision" }
              : { key: "dbot.summary.not-completed" },
    hasValidationError: !item.isEnabled,
    notReady: !isSendToBRReady(item, followUp),
    infoMessage: !item.isEnabled
      ? { key: "dbot.summary.disabledSendToBR" }
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.isSentToBR" }
        : data.brStatus === "DELETED"
          ? { key: "dbot.summary.sendToBR.skipped.description" }
          : followUp.status === FollowUpStatus.COMPLETED
            ? undefined
            : getDecisionSendToBRDescription(
                item.decision.type,
                data.isRegistration,
                item.followUps,
              ),
  };
}

export function getApplyToSystemPanel(item: DecisionListItem, followUp: FollowUp): DetailsPanel {
  return {
    type: "follow-up",
    id: "apply-to-system",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: !item.isEnabled
      ? "clock"
      : followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading:
      item.decision.type === DecisionType.incorporation
        ? { key: "dbot.summary.activateIncorporation" }
        : { key: "dbot.summary.applyDecisionToSystem" },
    hasValidationError: !item.isEnabled,
    label:
      followUp.status === FollowUpStatus.COMPLETED
        ? { key: "dbot.summary.completed" }
        : !item.isEnabled
          ? { key: "dbot.summary.missing-system-settings" }
          : !followUp.isActive
            ? { key: "dbot.summary.pending-decision" }
            : { key: "dbot.summary.not-completed" },
    notReady: !item.isEnabled || !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    infoMessage: !item.applied ? { key: "dbot.summary.applyDecisionToSystem.tooltip" } : undefined,
  };
}

export function getSubmitToRegisterOfCompanyAccountsPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const completed = followUp.status === FollowUpStatus.COMPLETED;
  return {
    type: "follow-up",
    id: "submitToRegisterOfCompanyAccounts",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: completed
      ? { name: "check", color: "success", weight: "bold" }
      : followUp.isActive
        ? { name: "arrow-right", color: "info" }
        : "clock",
    heading: { key: "dbot.follow-up.submitToRegisterOfCompanyAccounts.title" },
    hasValidationError: false,
    label: completed
      ? { key: "dbot.summary.completed" }
      : !followUp.isActive
        ? { key: "dbot.summary.pending-decision" }
        : { key: "dbot.summary.not-completed" },
    notReady: !followUp.isActive || completed,
  };
}

export function getRegisterInShareholderRegisterPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const completed = followUp.status === FollowUpStatus.COMPLETED;
  return {
    type: "follow-up",
    id: "registerInShareholderRegister",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: completed
      ? { name: "check", color: "success", weight: "bold" }
      : followUp.isActive
        ? { name: "arrow-right", color: "info" }
        : "clock",
    heading: { key: "dbot.follow-up.registerInShareholderRegister.title" },
    hasValidationError: false,
    label: completed
      ? { key: "dbot.summary.completed" }
      : !followUp.isActive
        ? { key: "dbot.summary.pending-decision" }
        : { key: "dbot.summary.not-completed" },
    notReady: !followUp.isActive || completed,
  };
}

const interimBalanceSheetAltinnUrls: Record<Language, string> = {
  [Language.NO]: "https://info.altinn.no/skjemaoversikt/bronnoysundregistrene/mellombalanse/",
  [Language.EN]:
    "https://info.altinn.no/en/forms-overview/bronnoysund-register-centre/interim-balance-sheet/",
  [Language.SV]:
    "https://info.altinn.no/en/forms-overview/bronnoysund-register-centre/interim-balance-sheet/",
};

export function getInterimBalanceSheetAltinnLink(locale: Language): {
  url: string;
  label: TMessage;
} {
  const url = interimBalanceSheetAltinnUrls[locale] ?? interimBalanceSheetAltinnUrls[Language.EN];
  const { host, pathname } = new URL(url);
  const page = pathname.split("/").filter(Boolean).at(-1);
  return {
    url,
    label: {
      key: "dbot.follow-up.submitToRegisterOfCompanyAccounts.continueButton",
      params: { link: page ? `${host}/.../${page}` : host },
    },
  };
}

export function getRequiredAttachmentsPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as AttachmentFollowUp;
  const uploadedAttachments = item.uploadedDecisionAttachments.filter(
    (a) => a.type === data.attachmentType,
  );
  return {
    type: "follow-up",
    id: data.attachmentType,
    followUp: {
      item: followUp,
      orgNo: item.orgNo,
      decisionId: item.decision.decisionId,
      attachmentType: data.attachmentType,
    },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: requiredAttachmentLabel(data.attachmentType),
    hasValidationError: false,
    label:
      uploadedAttachments.length > 1
        ? { key: "dbot.summary.multipleFiles", params: { n: uploadedAttachments.length } }
        : uploadedAttachments.length > 0 || followUp.status === FollowUpStatus.COMPLETED
          ? { key: "dbot.summary.completed" }
          : followUp.isActive
            ? { key: "dbot.summary.decisionRequiresDocumentation" }
            : { key: "dbot.summary.pending-decision" },
    action:
      uploadedAttachments.length === 1 && !isIdentificationAttachment(data.attachmentType)
        ? {
            label: { translated: uploadedAttachments[0].name },
            event: { type: "dbot/download-attachment", attachmentId: uploadedAttachments[0].id },
          }
        : undefined,
    notReady:
      followUp.isForInformation ||
      !followUp.isActive ||
      followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      uploadedAttachments.length === 0 && followUp.status !== FollowUpStatus.COMPLETED
        ? getDecisionAttachmentsDescription(data.attachmentType)
        : undefined,
  };
}

function openDocumentEvent(documentId: string | undefined): DbotEvent {
  return documentId === undefined
    ? { type: "dbot/open-tab", tab: "documents" }
    : { type: "dbot/toggle-document-panel", documentId, expanded: true };
}

function signingFollowUpIcon(status: FollowUpStatus): DetailsPanel["icon"] {
  return status === FollowUpStatus.COMPLETED
    ? { name: "check", color: "success", weight: "bold" }
    : status === FollowUpStatus.IN_PROGRESS
      ? "arrows-clockwise"
      : { name: "arrow-right", color: "info" };
}

function incorporationPartDocumentId(
  item: DecisionListItem,
  part: IncorporationDocumentPart,
): string | undefined {
  return item.documents?.find((d) => d.incorporationDocumentPart === part)?.document.documentId;
}

// The sealing date of the stiftelsesdokument must not be after the other incorporation documents,
// so none of them can be sent for signing until it is fully signed.
function awaitsIncorporationDocumentSigning(item: DecisionListItem, followUp: FollowUp): boolean {
  if (followUp.status !== FollowUpStatus.READY) {
    return false;
  }
  const signIncorporationDocument = item.followUps.find(
    (f) => f.data.type === FollowUpType.signIncorporationDocument,
  );
  return (
    signIncorporationDocument !== undefined &&
    signIncorporationDocument.status !== FollowUpStatus.COMPLETED
  );
}

export function getSignIncorporationDocumentStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: "signIncorporationDocument",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: signingFollowUpIcon(followUp.status),
    heading: {
      key: "dbot.follow-up.signIncorporationDocument.title",
    },
    hasValidationError: false,
    label: { key: followUpStatusLabels[followUp.status] },
    notReady: true,
    customOpenEvent: openDocumentEvent(
      item.documents?.find((d) => isIncorporationDocument(d.document))?.document.documentId,
    ),
  };
}

export function getSignBusinessInformationStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const isAwaiting = awaitsIncorporationDocumentSigning(item, followUp);
  return {
    type: "follow-up",
    id: "signBusinessInformationPanel",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: isAwaiting ? "clock" : signingFollowUpIcon(followUp.status),
    heading: {
      key: "dbot.follow-up.signBusinessInformationDocument.title",
    },
    hasValidationError: false,
    label: {
      key: isAwaiting ? "dbot.summary.awaiting" : followUpStatusLabels[followUp.status],
    },
    notReady: true,
    customOpenEvent: openDocumentEvent(incorporationPartDocumentId(item, "BUSINESS_INFORMATION")),
  };
}

export function getMergerOwnResolutionPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const decisionData = item.decision.decisionData;
  const merger = decisionData?.type === DecisionType.merger ? decisionData : undefined;
  const companyName = (merger && ownCompanyOf(merger)?.name) ?? "";
  const completed = followUp.status === FollowUpStatus.COMPLETED;
  return {
    type: "follow-up",
    id: "mergerOwnResolution",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: completed
      ? { name: "check", color: "success", weight: "bold" }
      : { name: "arrow-right", color: "info" },
    heading: {
      key: "dbot.follow-up.mergerOwnResolution.title",
      params: { companyName },
    },
    hasValidationError: false,
    label: { key: followUpStatusLabels[followUp.status] },
    notReady: completed,
    customOpenEvent: {
      type: "dbot/open-tab",
      tab: "documents",
    },
  };
}

export function getMergerCounterpartResolutionPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const decisionData = item.decision.decisionData;
  const merger = decisionData?.type === DecisionType.merger ? decisionData : undefined;
  const companyName = (merger && counterpartyOf(merger)?.name) ?? "";
  const completed = followUp.status === FollowUpStatus.COMPLETED;
  return {
    type: "follow-up",
    id: "mergerCounterpartResolution",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: completed
      ? { name: "check", color: "success", weight: "bold" }
      : { name: "arrow-right", color: "info" },
    heading: {
      key: "dbot.follow-up.mergerCounterpartResolution.title",
      params: { companyName },
    },
    hasValidationError: false,
    label: completed
      ? { key: "dbot.summary.completed" }
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.mergerCounterpart.waiting", params: { companyName } }
        : { key: "dbot.summary.not-completed" },
    notReady: completed,
  };
}

export function getSignMergerPlanStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: "signMergerPlan",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : { name: "arrow-right", color: "info" },
    heading: {
      key: "dbot.follow-up.signMergerPlan.title",
    },
    hasValidationError: false,
    label: { key: followUpStatusLabels[followUp.status] },
    notReady: true,
    customOpenEvent: {
      type: "dbot/open-tab",
      tab: "documents",
    },
  };
}

export function getSendMergerShareholderNoticePanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: "sendMergerShareholderNotice",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : { name: "arrow-right", color: "info" },
    heading: {
      key: "dbot.follow-up.sendMergerShareholderNotice.title",
    },
    hasValidationError: false,
    label: { key: followUpStatusLabels[followUp.status] },
    notReady: true,
    customOpenEvent: {
      type: "dbot/open-tab",
      tab: "documents",
    },
  };
}

export function getConfirmationOfPaidShareCapitalContributionStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const isAwaiting = awaitsIncorporationDocumentSigning(item, followUp);
  return {
    type: "follow-up",
    id: "confirmationOfPaidShareCapitalContributionPanel",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: isAwaiting ? "clock" : signingFollowUpIcon(followUp.status),
    heading: {
      key: "dbot.follow-up.confirmationOfPaidShareCapitalContribution.title",
    },
    hasValidationError: false,
    label: {
      key: isAwaiting ? "dbot.summary.awaiting" : followUpStatusLabels[followUp.status],
    },
    notReady: true,
    customOpenEvent: openDocumentEvent(
      incorporationPartDocumentId(item, "CONFIRMATION_OF_PAID_SHARE_CAPITAL_CONTRIBUTION"),
    ),
  };
}

export function getConfirmationOfAuditorRoleStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const isAwaiting = awaitsIncorporationDocumentSigning(item, followUp);
  return {
    type: "follow-up",
    id: "confirmationOfAuditorRolePanel",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: isAwaiting ? "clock" : signingFollowUpIcon(followUp.status),
    heading: {
      key: "dbot.follow-up.confirmationOfAuditorRole.title",
    },
    hasValidationError: false,
    label: {
      key: isAwaiting ? "dbot.summary.awaiting" : followUpStatusLabels[followUp.status],
    },
    notReady: true,
    customOpenEvent: openDocumentEvent(
      incorporationPartDocumentId(item, "CONFIRMATION_OF_AUDITOR_ROLE"),
    ),
  };
}

export function getConfirmationOfAccountantRoleStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const isAwaiting = awaitsIncorporationDocumentSigning(item, followUp);
  return {
    type: "follow-up",
    id: "confirmationOfAccountantRolePanel",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon: isAwaiting ? "clock" : signingFollowUpIcon(followUp.status),
    heading: {
      key: "dbot.follow-up.confirmationOfAccountantRole.title",
    },
    hasValidationError: false,
    label: {
      key: isAwaiting ? "dbot.summary.awaiting" : followUpStatusLabels[followUp.status],
    },
    notReady: true,
    customOpenEvent: openDocumentEvent(
      incorporationPartDocumentId(item, "CONFIRMATION_OF_ACCOUNTANT_ROLE"),
    ),
  };
}

export function getIncreaseOfShareCapitalSubscribersStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as ViewSubscribersFollowUp;
  const isSubscriptionForm = item.documents?.some(
    (d) => d.document.type === DocumentType.subscription_form,
  );

  return {
    type: "follow-up",
    id: "view-subscribers",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : "user-list",
    heading: isSubscriptionForm
      ? { key: "dbot.summary.subscribersRights.heading" }
      : {
          key: `dbot.summary.subscribersStatus.heading`,
        },
    hasValidationError: false,
    label: getSubscribersStatusLabel(data.numberOfSubscribers),
    customOpenEvent: {
      type: "dbot/open-increase-of-share-capital-subscribers-status",
      orgNo: item.orgNo,
      decisionId: item.decision.decisionId,
      isLocked: followUp.status === FollowUpStatus.COMPLETED,
    },
    notReady:
      followUp.isForInformation ||
      !followUp.isActive ||
      followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      followUp.status !== FollowUpStatus.COMPLETED
        ? { key: "dbot.summary.subscribersStatus.description.edit" }
        : undefined,
  };
}

export function getSubscribersStatusLabel(numberOfSubscribers: number): TMessage {
  return numberOfSubscribers === 0
    ? { key: "dbot.summary.subscribersStatus.label.noSubscribers" }
    : numberOfSubscribers === 1
      ? {
          key: "dbot.summary.subscribersStatus.label.subscriber",
          params: { numberOfSubscribers },
        }
      : {
          key: "dbot.summary.subscribersStatus.label.subscribers",
          params: { numberOfSubscribers },
        };
}

export function sendSubscriptionFormPanel(
  item: DecisionListItem,
  followUp: FollowUp,
  locale: Language,
): DetailsPanel {
  const data = followUp.data as SubscriptionFormFollowUp;
  const documentListItem = item.documents?.find((d) => d.document.documentId === data.documentId);

  return {
    type: "follow-up",
    id: "subscription-form",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: getDocumentLabel(DocumentType.subscription_form),
    label: getSubscriptionFormStatusLabel(followUp, documentListItem),
    customOpenEvent:
      documentListItem === undefined
        ? undefined
        : followUp.status === FollowUpStatus.READY
          ? (openSendForSigningDialogAction({
              document: documentListItem.document,
              signingStatus: documentListItem.signingStatus,
              showUnsupportedAttachmentWarning: false,
            }) ?? undefined)
          : {
              document: documentListItem?.document,
              type: "dbot/open-signing-status-dialog",
              label: { key: "show_status" },
            },
    notReady: !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    isDisabled: !followUp.isActive || documentListItem === undefined,
    infoMessage:
      documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
        ? undefined
        : followUp.status === FollowUpStatus.COMPLETED
          ? {
              key: "dbot.summary.subscriptionForm.description.sent",
              params: {
                timestamp: formatDate(locale, documentListItem?.sendingTime),
              },
            }
          : { key: "dbot.summary.subscriptionForm.description.sendToSubscribers" },
  };
}

export function getSubscriptionFormStatusLabel(
  followUp: FollowUp,
  documentListItem: DocumentListItem | undefined,
): TMessage {
  return documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
    ? { key: "dbot.summary.completed" }
    : followUp.status === FollowUpStatus.COMPLETED
      ? { key: "dbot.summary.signingStatus.sent" }
      : followUp.isActive
        ? { key: "dbot.summary.signingStatus.not_sent" }
        : { key: "dbot.summary.pending-decision" };
}

export function viewSubscriptionFormStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as SubscriptionFormStatusFollowUp;
  const documentListItem = item.documents?.find((d) => d.document.documentId === data.documentId);

  return {
    type: "follow-up",
    id: "view-subscription-form-status",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: { key: "dbot.summary.subscribersStatus.heading" },
    customOpenEvent:
      documentListItem === undefined
        ? undefined
        : {
            document: documentListItem.document,
            label: { key: "show_status" },
            type: "dbot/open-subscription-status",
          },
    notReady: true,
    isDisabled: !followUp.isActive || documentListItem === undefined,
  };
}

export function increaseOfShareCapitalSubscribersAllocationPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: "allocate-shares",
    followUp: { item: followUp, orgNo: item.orgNo, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: { key: "dbot.summary.subscribersAllocation.title" },
    label: getCommonFollowUpStatusLabel(followUp),
    customOpenEvent: {
      type: "dbot/open-increase-of-share-capital-subscribers-allocation",
      orgNo: item.orgNo,
      decisionId: item.decision.decisionId,
      isSentToBR: item.followUps.some(
        (f) => f.data.type === FollowUpType.sendToBR && f.data.brStatus !== "NOT_SENT",
      ),
    },
    notReady: !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      followUp.status === FollowUpStatus.COMPLETED
        ? undefined
        : followUp.isActive
          ? { key: "dbot.summary.subscribersAllocation.description" }
          : { key: "dbot.summary.subscribersAllocation.description.disabled" },
  };
}

export function getCommonFollowUpStatusLabel(followUp: FollowUp): TMessage | undefined {
  switch (followUp.status) {
    case FollowUpStatus.IN_PROGRESS:
      return { key: "dbot.summary.inProgress" };
    case FollowUpStatus.COMPLETED:
      return undefined;
    default:
      return !followUp.isActive
        ? { key: "dbot.summary.pending-decision" }
        : { key: "dbot.summary.not-completed" };
  }
}

export function getDecisionFollowUpPanels(
  item: DecisionListItem,
  locale: Language,
): DetailsPanel[] {
  return item.followUps.map((followUp) => {
    switch (followUp.data.type) {
      case FollowUpType.signIncorporationDocument:
        return getSignIncorporationDocumentStatusPanel(item, followUp);
      case FollowUpType.signBusinessInformation:
        return getSignBusinessInformationStatusPanel(item, followUp);
      case FollowUpType.signMergerPlan:
        return getSignMergerPlanStatusPanel(item, followUp);
      case FollowUpType.sendMergerShareholderNotice:
        return getSendMergerShareholderNoticePanel(item, followUp);
      case FollowUpType.mergerOwnResolution:
        return getMergerOwnResolutionPanel(item, followUp);
      case FollowUpType.mergerCounterpartResolution:
        return getMergerCounterpartResolutionPanel(item, followUp);
      case FollowUpType.confirmationOfPaidShareCapitalContribution:
        return getConfirmationOfPaidShareCapitalContributionStatusPanel(item, followUp);
      case FollowUpType.confirmationOfAuditorRole:
        return getConfirmationOfAuditorRoleStatusPanel(item, followUp);
      case FollowUpType.confirmationOfAccountantRole:
        return getConfirmationOfAccountantRoleStatusPanel(item, followUp);
      case FollowUpType.viewSubscribers:
        return getIncreaseOfShareCapitalSubscribersStatusPanel(item, followUp);
      case FollowUpType.subscriptionForm:
        return sendSubscriptionFormPanel(item, followUp, locale);
      case FollowUpType.subscriptionFormStatus:
        return viewSubscriptionFormStatusPanel(item, followUp);
      case FollowUpType.allocatedShares:
        return increaseOfShareCapitalSubscribersAllocationPanel(item, followUp);
      case FollowUpType.attachment:
        return getRequiredAttachmentsPanel(item, followUp);
      case FollowUpType.identification:
        return getRequiredAttachmentsPanel(item, followUp);
      case FollowUpType.sendToBR:
        return getSendToBRPanel(item, followUp, followUp.data);
      case FollowUpType.apply:
        return getApplyToSystemPanel(item, followUp);
      case FollowUpType.submitToRegisterOfCompanyAccounts:
        return getSubmitToRegisterOfCompanyAccountsPanel(item, followUp);
      case FollowUpType.registerInShareholderRegister:
        return getRegisterInShareholderRegisterPanel(item, followUp);
    }
  });
}

export function getActiveFollowUpPanels(items: DecisionListItem[], l: Language): number {
  return sumBy(
    items,
    (item) => getDecisionFollowUpPanels(item, l).filter((d) => !d.notReady).length,
  );
}

export function canBeSentForVoting(decisions: DecisionItem[], document: DocumentItem) {
  if (decisions.some((d) => d.decisionData?.type !== DecisionType.freetext)) {
    return true;
  } else {
    if (
      decisions.some(
        (d) =>
          d.decisionData?.type === DecisionType.freetext &&
          d.decisionData.freetextTypeForAllDocuments === FreetextType.DECISION_MATTER,
      )
    ) {
      return true;
    }

    return decisions.some(
      (d) =>
        d.decisionData?.type === DecisionType.freetext &&
        d.decisionData.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED &&
        d.decisionData.customizedFreetextTypePerDocument.length > 0 &&
        d.decisionData.customizedFreetextTypePerDocument.some(
          (c) =>
            c.documentType === document.type &&
            c.customizedFreetextType === CustomizedFreetextType.DECISION_MATTER,
        ),
    );
  }
}

function generatePinCode() {
  return Math.floor(Math.random() * 9999)
    .toString()
    .padStart(4, "0");
}

export function findProjectBoardMeetingFolder(
  entries: DestinationFolder[],
  projectId: string,
): DestinationFolder | undefined {
  return entries?.find(
    (f) => f.projectId === projectId && f.meetingType === MeetingType.BOARD_MEETING,
  );
}

export function containsSendToBRRegistration(followUps: FollowUp[]) {
  return followUps.some((d) => d.data.type === FollowUpType.sendToBR && d.data.isRegistration);
}

export function enableSendToBRDate(decisionType: DecisionType, followUps?: FollowUp[]) {
  const sendToBRRegistration = followUps?.find(
    (d) => d.data.type === FollowUpType.sendToBR && d.data.isRegistration,
  );
  if (sendToBRRegistration?.data.type !== FollowUpType.sendToBR) {
    return undefined;
  }

  switch (decisionType) {
    case DecisionType.reductionOfShareCapital:
    case DecisionType.merger: {
      return sendToBRRegistration?.data.brPublishingDate
        ? plusDays(toDate(sendToBRRegistration?.data.brPublishingDate), 42)
        : undefined;
    }
    default:
      return undefined;
  }
}
